<template>
  <div
    v-if="session.character"
    class="shell"
    :class="{ 'drawer-open': drawerOpen, loading: isLocationTransitioning || isShellLoading }"
    :style="shellStyle"
    :data-mobile="isMobile ? 'true' : undefined"
  >
    <div v-if="isLocationTransitioning || isShellLoading" class="shell-loading" aria-live="polite" aria-busy="true">
      <div class="shell-loading__card">
        <div class="spinner" aria-hidden="true"></div>
        <div style="font-weight: 700;">{{ shellLoadingTitle }}</div>
        <div class="text-muted" style="font-size: 0.9rem;">{{ shellLoadingSubtitle }}</div>
        <div v-if="isShellLoading && areaBootstrapError" style="margin-top: 0.9rem;">
          <div class="text-muted" style="font-size: 0.9rem;">
            {{ areaBootstrapError }}
          </div>
          <div style="display: flex; gap: 0.5rem; justify-content: center; margin-top: 0.75rem;">
            <UiButton size="sm" variant="primary" @click="retryAreaBootstrap">Retry now</UiButton>
            <UiButton size="sm" variant="secondary" @click="router.push('/characters')">Back</UiButton>
          </div>
        </div>
      </div>
    </div>

    <aside class="shell-rail">
      <div class="rail-brand">
        <span class="rail-brand__tag">Idle RPG</span>
        <span class="rail-brand__title">Arcane World</span>
      </div>

      <UiPanel class="rail-identity" raised>
        <div class="rail-identity__name">{{ session.character.name }}</div>
        <div class="rail-identity__meta">
          {{ session.character.classId }} • {{ area?.name || "Unknown" }}
        </div>
        <div class="rail-identity__connection" data-testid="session-connection-indicator">
          <UiChip :variant="connectionChip.variant" class="stat-mono num">
            {{ connectionChip.label }}
          </UiChip>
        </div>
        <div class="rail-vitals">
          <div class="rail-vital">
            <span>HP</span>
            <span class="stat-mono num">
              {{ formatHpWithTemp(session.character.hp, session.character.temp_hp) }} / {{ formatNumber(session.character.max_hp) }}
            </span>
          </div>
          <UiProgressBar :value="hpPercent" tone="combat" />
          <div class="rail-vital">
            <span>Mana</span>
            <span class="stat-mono num">
              {{ formatNumber(session.character.mana) }} / {{ formatNumber(session.character.max_mana) }}
            </span>
          </div>
          <UiProgressBar :value="manaPercent" tone="social" />
        </div>
        <div class="rail-activity">
          <UiChip variant="muted">Now doing</UiChip>
          <div class="rail-activity__text">{{ currentActivityLabel }}</div>
        </div>
        <div v-if="freshnessChip" class="rail-freshness">
          <UiChip :variant="freshnessChip.variant" class="stat-mono num">
            {{ freshnessChip.icon }} {{ freshnessChip.label }}
          </UiChip>
        </div>
      </UiPanel>

      <div class="rail-cta">
        <UiButton
          v-if="pendingLevelups > 0"
          variant="primary"
          size="sm"
          @click="openLevelUpOverlay"
        >
          <template #icon>{{ ICONS.actions.levelUp }}</template>
          Level Up
          <UiBadge variant="accent">{{ pendingLevelups }}</UiBadge>
        </UiButton>
        <UiButton
          v-if="apAvailable > 0"
          variant="secondary"
          size="sm"
          @click="openSpendApOverlay"
        >
          <template #icon>{{ ICONS.actions.spendAp }}</template>
          Spend AP
          <UiBadge variant="accent">{{ apAvailable }}</UiBadge>
        </UiButton>
      </div>

      <div v-if="!isMobile" class="rail-status">
        <UiPanel class="rail-status__panel" tone="work">
          <div class="rail-status__header">
            <span class="stat-mono num">Status</span>
            <UiChip v-if="inCombat" variant="danger">{{ ICONS.domains.combat }} Combat</UiChip>
            <UiChip v-else-if="travel" variant="accent">{{ ICONS.domains.travel }} Travel</UiChip>
          </div>

          <div v-if="processingAction" class="rail-status__item">
            <div>
              <div class="rail-status__title">Processing</div>
              <div class="text-muted rail-status__desc">{{ processingAction.recipeName }}</div>
            </div>
            <div class="rail-status__actions">
              <UiButton size="sm" variant="secondary" @click="openSession('workshop')">Open</UiButton>
              <UiButton
                size="sm"
                :variant="processingAction.isPaused ? 'primary' : 'secondary'"
                @click="processingAction.isPaused ? resumeProcessing() : pauseProcessing()"
              >
                {{ processingAction.isPaused ? "Resume" : "Pause" }}
              </UiButton>
            </div>
          </div>

          <div v-else-if="castingAction" class="rail-status__item">
            <div>
              <div class="rail-status__title">Casting</div>
              <div class="text-muted rail-status__desc">{{ castingAction.spellName }}</div>
            </div>
            <div class="rail-status__actions">
              <UiButton size="sm" variant="secondary" @click="openSession('magic')">Open</UiButton>
              <UiButton
                v-if="castingAction.isPaused"
                size="sm"
                variant="primary"
                @click="resumeCasting()"
              >
                Resume
              </UiButton>
              <UiButton
                v-else
                size="sm"
                variant="secondary"
                @click="cancelCasting()"
              >
                Stop
              </UiButton>
            </div>
          </div>

          <div v-else-if="gatheringAction" class="rail-status__item">
            <div>
              <div class="rail-status__title">Gathering</div>
              <div class="text-muted rail-status__desc">{{ currentActivityLabel }}</div>
            </div>
            <div class="rail-status__actions">
              <UiButton size="sm" variant="danger" @click="stopGathering()">Stop</UiButton>
            </div>
          </div>

          <div v-else class="rail-status__empty text-muted">
            No active actions. Use sessions to start gathering, processing, or casting.
          </div>
        </UiPanel>
      </div>

      <div v-if="!isMobile" class="rail-utility">
        <UiButton variant="secondary" size="sm" @click.stop="openCharacterSwitcher">
          <template #icon>{{ ICONS.actions.switchCharacter }}</template>
          Switch
        </UiButton>
        <UiButton variant="ghost" size="sm" @click="logoutFromShell">
          <template #icon>{{ ICONS.actions.logout }}</template>
          Logout
        </UiButton>
      </div>

      <nav v-else class="mobile-nav" aria-label="Bottom navigation">
        <button
          v-for="item in bottomNavItems"
          :key="item.id"
          class="mobile-nav__item"
          :class="{ active: item.id === activeSession }"
          type="button"
          @click="item.action()"
        >
          <span class="mobile-nav__icon" aria-hidden="true">{{ item.icon }}</span>
          <span class="mobile-nav__label">{{ item.label }}</span>
        </button>
      </nav>
    </aside>

    <main class="shell-world" @click.self="handleWorldClick">
      <header class="world-header">
        <div class="world-header__title">
          <h2>{{ area?.name || "Area" }}</h2>
          <p class="text-muted">{{ area?.description }}</p>
          <div class="world-header__location" v-if="currentLocation">
            <UiChip variant="accent">Visiting</UiChip>
            <span>{{ currentLocation.name }}</span>
            <UiChip v-if="currentStationLabel" variant="muted">{{ ICONS.places.station }} {{ currentStationLabel }}</UiChip>
          </div>
        </div>

        <div class="world-header__actions">
          <div v-if="inCombat" class="world-header__warning">{{ ICONS.domains.combat }} Cannot travel during combat</div>
          <div class="row wrap world-header__sessions" style="justify-content: flex-end;">
            <UiButton variant="secondary" size="sm" @click="openSession('workshop')" :disabled="isLocationTransitioning">
              <template #icon>{{ ICONS.sessions.workshop }}</template>
              Workshop
            </UiButton>
            <UiButton
              variant="secondary"
              size="sm"
              v-if="bankAvailableHere"
              @click="openSession('bank')"
              :disabled="isLocationTransitioning"
            >
              <template #icon>{{ ICONS.sessions.bank }}</template>
              Bank
            </UiButton>
            <UiButton
              variant="secondary"
              size="sm"
              v-if="bankAvailableHere"
              @click="openSession('exchange')"
              :disabled="isLocationTransitioning"
            >
              <template #icon>{{ ICONS.sessions.exchange }}</template>
              Exchange
            </UiButton>
            <UiButton variant="secondary" size="sm" @click="openSession('social')" :disabled="isLocationTransitioning">
              <template #icon>{{ ICONS.sessions.social }}</template>
              Social
            </UiButton>
          </div>

          <div v-if="travel" class="travel-banner">
            <div class="travel-banner__route">
              On the road: {{ AREA_NAMES[travel.fromAreaId] || travel.fromAreaId }} → {{ AREA_NAMES[travel.toAreaId] || travel.toAreaId }}
            </div>
            <div class="text-muted">
              Arriving in <span class="stat-mono num">{{ formatMs(travelRemainingMs) }}</span>
            </div>
            <div class="row" style="justify-content: flex-end;">
              <UiButton variant="danger" size="sm" @click="turnTravel()" :disabled="inCombat || browserOffline || !sessionSocket.isConnected.value">Turn</UiButton>
              <UiButton size="sm" @click="travel.isPaused ? walkTravel() : restTravel()" :disabled="inCombat || browserOffline || !sessionSocket.isConnected.value">
                {{ travel.isPaused ? "Walk" : "Rest" }}
              </UiButton>
            </div>
          </div>

          <div v-else class="row wrap world-header__travel" style="justify-content: flex-end;">
            <UiButton
              v-for="adj in adjacentAreas"
              :key="adj.id"
              size="sm"
              @click="openTravelModal(adj.id)"
              :disabled="inCombat || isInBuilding || isLocationTransitioning || browserOffline || !sessionSocket.isConnected.value"
            >
              {{ adj.name }}
            </UiButton>
          </div>
        </div>
      </header>

      <div v-if="encounter" class="encounter-float">
        <EncounterOverlay
          :encounter="encounter"
          :playerId="playerIdRef"
          :readiedSpells="readiedSpellsRef"
          :turnTimeRemaining="turnTimeRemaining"
          :localLogLines="localEncounterLog"
          :consumables="consumableItems"
          :canUseConsumable="canUseConsumable"
          :useDisabledReason="consumableDisabledReason"
          :usingItemId="usingItemId"
          :bonusActionUsed="bonusActionUsed"
          :loadout="combatLoadout"
          :reloadOptions="reloadOptions"
          :canReload="canReload"
          :reloadDisabledReason="reloadDisabledReason"
          :hotSwapOptions="hotSwapOptions"
          :canHotSwap="canHotSwap"
          :hotSwapDisabledReason="hotSwapDisabledReason"
          :turnSubmitting="turnSubmitting"
          :onDismiss="endEncounter"
          :onAction="(action) => submitAction(action)"
          :onCastSpell="castSpell"
          :onReload="handleReload"
          :onHotSwap="handleHotSwap"
          :onUseConsumable="useConsumable"
        />
      </div>

      <div class="world-grid">
        <UiPanel v-if="showBuildingsPanel">
          <BuildingsPanel
            :locations="locations"
            :currentLocationId="currentLocationId"
            :inCombat="inCombat"
            :isLoading="isLocationTransitioning"
            @enter="enterLocation"
            @exit="exitLocation"
            @open-service="openSession"
          />
        </UiPanel>

        <UiPanel v-if="showNpcsPanel">
          <NpcsPanel
            :npcs="npcs"
            :disabled="isLocationTransitioning || !!encounter"
            @engage="startCombat"
            @trade="openShop"
            @talk="handleSocialHint"
            @influence="handleSocialHint"
            @inspect="handleInspect"
            @steal="handleSteal"
          />
        </UiPanel>

        <UiPanel v-if="showPlayersPanel">
          <PlayersPanel :players="players" @inspect="inspectPlayer" />
        </UiPanel>

        <UiPanel v-if="showGroundLootPanel">
          <GroundLootPanel
            :groundLoot="groundLoot"
            :disabled="!!encounter"
            :currentCharacterId="session.character.id"
            @take="takeGroundLoot"
          />
        </UiPanel>

        <UiPanel v-if="showResourcesPanel">
          <ResourcesPanel
            :resourceNodes="resourceNodes"
            :isGathering="isGathering"
            :gatheringAction="gatheringAction"
            :nodeDepleted="gatheringResults?.nodeDepleted ?? false"
            :skillProgress="session.character?.skillProgress ?? []"
            :effectSums="effectSums"
            :equipment="session.character.equipment"
            :inventory="session.character.inventory"
            :disabled="!!encounter || isLocationTransitioning"
            @gather="startGathering"
            @stop="stopGathering"
          />
        </UiPanel>
      </div>
    </main>

    <div v-if="drawerOpen && !isDrawerSheet" class="drawer-scrim" @click="collapseDrawer"></div>

    <aside v-if="!isDrawerSheet" class="shell-drawer" :class="{ open: drawerOpen }">
      <div class="drawer-spine">
        <UiIconButton class="drawer-toggle" variant="ghost" size="sm" @click="toggleDrawer" aria-label="Toggle drawer">
          {{ drawerOpen ? "«" : "»" }}
        </UiIconButton>

        <div class="drawer-spine__status">
          <div class="spine-stat">
            <div class="spine-stat__label">HP</div>
            <div class="spine-stat__value stat-mono num">{{ formatHpWithTemp(session.character.hp, session.character.temp_hp) }}</div>
          </div>
          <div class="spine-stat">
            <div class="spine-stat__label">MP</div>
            <div class="spine-stat__value stat-mono num">{{ formatNumber(session.character.mana) }}</div>
          </div>

          <UiChip
            v-if="processingAction?.isPaused || castingAction?.isPaused || travel?.isPaused"
            variant="paused"
            class="spine-paused-chip"
          >
            Paused
          </UiChip>
        </div>

        <div v-if="hasSpineQuickActions" class="drawer-spine__quick">
          <UiIconButton
            v-if="processingAction"
            :variant="processingAction.isPaused ? 'primary' : 'secondary'"
            size="sm"
            :title="processingAction.isPaused ? 'Resume processing' : 'Pause processing'"
            @click="processingAction.isPaused ? resumeProcessing() : pauseProcessing()"
          >
            {{ processingAction.isPaused ? ICONS.states.resume : ICONS.states.paused }}
          </UiIconButton>

          <UiIconButton
            v-if="castingAction"
            :variant="castingAction.isPaused ? 'primary' : 'secondary'"
            size="sm"
            :title="castingAction.isPaused ? 'Resume casting' : 'Open Magic'"
            @click="castingAction.isPaused ? resumeCasting() : openSession('magic')"
          >
            {{ castingAction.isPaused ? ICONS.states.resume : ICONS.sessions.magic }}
          </UiIconButton>

          <UiIconButton
            v-if="gatheringAction"
            variant="secondary"
            size="sm"
            title="Stop gathering"
            @click="stopGathering()"
          >
            {{ ICONS.states.danger }}
          </UiIconButton>
        </div>

        <div class="drawer-icons">
          <UiTooltip
            v-for="sessionItem in sessionItems"
            :key="sessionItem.id"
            :hoverDelay="360"
          >
            <template #content>{{ sessionItem.label }}</template>
            <UiIconButton
              class="drawer-icon"
              :class="{ active: activeSession === sessionItem.id }"
              variant="ghost"
              size="sm"
              :aria-label="`Open ${sessionItem.label}`"
              @click="handleSessionIconClick(sessionItem.id)"
            >
              <span aria-hidden="true">{{ sessionItem.icon }}</span>
              <span class="sr-only">{{ sessionItem.label }}</span>
            </UiIconButton>
          </UiTooltip>
        </div>
      </div>

      <div class="drawer-panel">
        <div class="drawer-resize" @pointerdown="startDrawerResize" aria-hidden="true"></div>
        <header class="drawer-header">
          <div>
            <h3>{{ activeSessionLabel }}</h3>
            <p class="text-muted">{{ activeSessionDescription }}</p>
          </div>
          <UiIconButton variant="ghost" @click="collapseDrawer" :aria-label="`Close ${activeSessionLabel}`">
            {{ ICONS.actions.close }}
          </UiIconButton>
        </header>

        <div class="drawer-content scroll">
          <div v-if="activeSession === 'area'" class="area-session">
            <UiPanel>
              <UiSectionHeader>
                <template #title>Area Snapshot</template>
              </UiSectionHeader>
              <div class="area-session__meta">
                <div>
                  <div class="stats-label">Current Area</div>
                  <div>{{ area?.name || session.character.areaId }}</div>
                </div>
                <div>
                  <div class="stats-label">Location</div>
                  <div>{{ currentLocation?.name || "Outside" }}</div>
                </div>
                <div>
                  <div class="stats-label">Travel Status</div>
                  <div>{{ travel ? "On the road" : "Stationary" }}</div>
                </div>
              </div>
              <div class="area-session__actions">
                <UiButton size="sm" variant="secondary" @click="openSession('workshop')">Open Workshop</UiButton>
                <UiButton size="sm" variant="secondary" @click="openSession('inventory')">Open Inventory</UiButton>
              </div>
            </UiPanel>
            <UiPanel>
              <UiSectionHeader>
                <template #title>Adjacent Travel</template>
              </UiSectionHeader>
              <div class="area-session__travel">
                <UiButton
                  v-for="adj in adjacentAreas"
                  :key="adj.id"
                  size="sm"
                  @click="openTravelModal(adj.id)"
              :disabled="inCombat || isInBuilding || isLocationTransitioning || browserOffline || !sessionSocket.isConnected.value"
                >
                  {{ adj.name }}
                </UiButton>
              </div>
            </UiPanel>
          </div>

          <InventorySession
            v-else-if="activeSession === 'inventory'"
            :items="session.character.inventory"
            :gold="session.character.gold"
            :carry="carryState"
            :canUseConsumables="canUseConsumable"
            :useDisabledReason="consumableDisabledReason || undefined"
            :usingItemId="usingItemId"
            @drop="dropFromInventoryOrGold"
            @use="useConsumable"
            @open-containers="openContainers"
          />

          <EquipmentSession
            v-else-if="activeSession === 'equipment'"
            :equipment="session.character.equipment"
            :armorProficiencies="session.character.armorProficiencies"
            :weaponProficiencies="session.character.weaponProficiencies"
            @unequip="handleUnequip"
          />

          <SkillsSession
            v-else-if="activeSession === 'skills'"
            :skills="session.character.skillProgress"
            :lastUsedSkillId="lastUsedSkillId"
            :skillModifiers="effectSums?.skill ?? null"
          />

          <MagicSession
            v-else-if="activeSession === 'magic'"
            :castingAction="castingAction"
            :spellbook="spellbook"
            @resume="resumeCasting"
            @cancel="cancelCasting"
            @cast="castFromSession"
            @ready="readySpell"
            @unready="unreadySpell"
          />

          <StatsSession
            v-else-if="activeSession === 'stats'"
            :characterName="session.character.name"
            :characterClassId="session.character.classId"
            :areaName="area?.name || 'Unknown'"
            :currentActivityLabel="currentActivityLabel"
            :freshnessChip="freshnessChip"
            :hp="session.character.hp"
            :maxHp="session.character.max_hp"
            :tempHp="session.character.temp_hp ?? 0"
            :mana="session.character.mana"
            :maxMana="session.character.max_mana"
            :attributes="session.character.attributes"
            :attributeModifiers="effectSums?.attr ?? null"
            :activeEffects="activeEffects"
            :armorProficiencies="session.character.armorProficiencies"
            :weaponProficiencies="session.character.weaponProficiencies"
            @open-session="openSession"
          />

          <WorkshopSession
            v-else-if="activeSession === 'workshop'"
            v-model:selectedProcessingSkillId="workshopSelectedSkillId"
            v-model:selectedProcessingRecipeId="workshopSelectedRecipeId"
            :recipes="processingRecipes"
            :processingAction="processingAction"
            :inventory="session.character.inventory"
            :equipment="session.character.equipment"
            :currentLocationId="currentLocationId"
            :enchantments="enchantments"
            :skillProgress="session.character.skillProgress"
            :skillModifiers="effectSums?.skill ?? null"
            :currentLocationName="currentLocation?.name ?? null"
            :currentStationLabel="currentStationLabel"
            :currentMana="session.character.mana"
            :disabled="isLocationTransitioning"
            @start-processing="onStartProcessing"
            @pause-processing="pauseProcessing"
            @resume-processing="resumeProcessing"
            @cancel-processing="cancelProcessing"
            @apply-enchantment="applyEnchantment"
            @remove-enchantment="removeEnchantment"
          />

          <BankSession
            v-else-if="activeSession === 'bank'"
            :available="bankAvailableHere"
            :blockedReason="bankBlockedReason"
            :initialTab="bankTab"
          />

          <ExchangeSession
            v-else-if="activeSession === 'exchange'"
            :available="bankAvailableHere"
            :blockedReason="exchangeBlockedReason"
          />

          <ShopSession
            v-else-if="activeSession === 'shop'"
            :npcId="shopNpc?.templateId ?? shopNpc?.id ?? null"
            :npcName="shopNpc?.name ?? null"
          />

          <SocialSession
            v-else-if="activeSession === 'social' && session.character && session.token"
            :token="session.token"
            :characterId="session.character.id"
            :currentLocationId="currentLocationId"
            :locations="locations"
            :focusNpcStateId="socialNpcStateId"
            @location-changed="loadArea"
          />

          <LogSession v-else-if="activeSession === 'log'" />

          <div v-else class="session-placeholder">
            <p>{{ activeSessionLabel }} session is being wired into the new drawer. Stay tuned.</p>
            <UiButton variant="secondary" @click="collapseDrawer">Close</UiButton>
          </div>
        </div>
      </div>
    </aside>

    <UiSheet
      v-else
      :open="drawerOpen"
      :snapPoints="SHEET_SNAP_POINTS"
      :initialSnap="drawerSheetSnap"
      @update:open="(open) => (drawerOpen = open)"
      @snap="handleDrawerSheetSnap"
    >
      <div class="sheet-header">
        <div style="min-width: 0;">
          <div class="sheet-title">{{ activeSessionLabel }}</div>
          <div class="text-muted sheet-subtitle">{{ activeSessionDescription }}</div>
        </div>
        <UiIconButton variant="ghost" size="sm" @click="collapseDrawer" :aria-label="`Close ${activeSessionLabel}`">
          {{ ICONS.actions.close }}
        </UiIconButton>
      </div>

      <div class="drawer-content scroll">
        <!-- reuse the same session content -->
        <div v-if="activeSession === 'area'" class="area-session">
          <UiPanel>
            <UiSectionHeader>
              <template #title>Area Snapshot</template>
            </UiSectionHeader>
            <div class="area-session__meta">
              <div>
                <div class="stats-label">Current Area</div>
                <div>{{ area?.name || session.character.areaId }}</div>
              </div>
              <div>
                <div class="stats-label">Location</div>
                <div>{{ currentLocation?.name || "Outside" }}</div>
              </div>
              <div>
                <div class="stats-label">Travel Status</div>
                <div>{{ travel ? "On the road" : "Stationary" }}</div>
              </div>
            </div>
            <div class="area-session__actions">
              <UiButton size="sm" variant="secondary" @click="openSession('workshop')">Open Workshop</UiButton>
              <UiButton size="sm" variant="secondary" @click="openSession('inventory')">Open Inventory</UiButton>
            </div>
          </UiPanel>
          <UiPanel>
            <UiSectionHeader>
              <template #title>Adjacent Travel</template>
            </UiSectionHeader>
            <div class="area-session__travel">
              <UiButton
                v-for="adj in adjacentAreas"
                :key="adj.id"
                size="sm"
                @click="openTravelModal(adj.id)"
                :disabled="inCombat || isInBuilding || isLocationTransitioning || browserOffline || !sessionSocket.isConnected.value"
              >
                {{ adj.name }}
              </UiButton>
            </div>
          </UiPanel>
        </div>

        <InventorySession
          v-else-if="activeSession === 'inventory'"
          :items="session.character.inventory"
          :gold="session.character.gold"
          :carry="carryState"
          :canUseConsumables="canUseConsumable"
          :useDisabledReason="consumableDisabledReason || undefined"
          :usingItemId="usingItemId"
          @drop="dropFromInventoryOrGold"
          @use="useConsumable"
          @open-containers="openContainers"
        />

        <EquipmentSession
          v-else-if="activeSession === 'equipment'"
          :equipment="session.character.equipment"
          @unequip="handleUnequip"
        />

        <SkillsSession
          v-else-if="activeSession === 'skills'"
          :skills="session.character.skillProgress"
          :lastUsedSkillId="lastUsedSkillId"
          :skillModifiers="effectSums?.skill ?? null"
        />

        <MagicSession
          v-else-if="activeSession === 'magic'"
          :castingAction="castingAction"
          :spellbook="spellbook"
          @resume="resumeCasting"
          @cancel="cancelCasting"
          @cast="castFromSession"
          @ready="readySpell"
          @unready="unreadySpell"
        />

        <StatsSession
          v-else-if="activeSession === 'stats'"
          :characterName="session.character.name"
          :characterClassId="session.character.classId"
          :areaName="area?.name || 'Unknown'"
          :currentActivityLabel="currentActivityLabel"
          :freshnessChip="freshnessChip"
          :hp="session.character.hp"
          :maxHp="session.character.max_hp"
          :tempHp="session.character.temp_hp ?? 0"
          :mana="session.character.mana"
          :maxMana="session.character.max_mana"
          :attributes="session.character.attributes"
          :attributeModifiers="effectSums?.attr ?? null"
          :activeEffects="activeEffects"
          :armorProficiencies="session.character.armorProficiencies"
          :weaponProficiencies="session.character.weaponProficiencies"
          @open-session="openSession"
        />

        <WorkshopSession
          v-else-if="activeSession === 'workshop'"
          v-model:selectedProcessingSkillId="workshopSelectedSkillId"
          v-model:selectedProcessingRecipeId="workshopSelectedRecipeId"
          :recipes="processingRecipes"
          :processingAction="processingAction"
          :inventory="session.character.inventory"
          :equipment="session.character.equipment"
          :currentLocationId="currentLocationId"
          :enchantments="enchantments"
          :skillProgress="session.character.skillProgress"
          :skillModifiers="effectSums?.skill ?? null"
          :currentLocationName="currentLocation?.name ?? null"
          :currentStationLabel="currentStationLabel"
          :currentMana="session.character.mana"
          :disabled="isLocationTransitioning"
          @start-processing="onStartProcessing"
          @pause-processing="pauseProcessing"
          @resume-processing="resumeProcessing"
          @cancel-processing="cancelProcessing"
          @apply-enchantment="applyEnchantment"
          @remove-enchantment="removeEnchantment"
        />

        <BankSession
          v-else-if="activeSession === 'bank'"
          :available="bankAvailableHere"
          :blockedReason="bankBlockedReason"
          :initialTab="bankTab"
        />

        <ExchangeSession
          v-else-if="activeSession === 'exchange'"
          :available="bankAvailableHere"
          :blockedReason="exchangeBlockedReason"
        />

        <ShopSession
          v-else-if="activeSession === 'shop'"
          :npcId="shopNpc?.templateId ?? shopNpc?.id ?? null"
          :npcName="shopNpc?.name ?? null"
        />

        <SocialSession
          v-else-if="activeSession === 'social' && session.character && session.token"
          :token="session.token"
          :characterId="session.character.id"
          :currentLocationId="currentLocationId"
          :locations="locations"
          :focusNpcStateId="socialNpcStateId"
          @location-changed="loadArea"
        />

        <LogSession v-else-if="activeSession === 'log'" />

        <div v-else class="session-placeholder">
          <p>{{ activeSessionLabel }} session is being wired into the new drawer. Stay tuned.</p>
          <UiButton variant="secondary" @click="collapseDrawer">Close</UiButton>
        </div>
      </div>
    </UiSheet>

    <ActionDock
      :processingAction="processingAction"
      :gatheringAction="gatheringAction"
      :castingAction="castingAction"
      :resourceNodes="resourceNodes"
      :nodeDepleted="gatheringResults?.nodeDepleted ?? false"
      :processingRecipe="activeProcessingRecipe"
      :inventory="session.character.inventory"
      @open-session="openSession"
      @pause-processing="pauseProcessing"
      @resume-processing="resumeProcessing"
      @cancel-processing="cancelProcessing"
      @stop-gathering="stopGathering"
      @resume-casting="resumeCasting"
      @cancel-casting="cancelCasting"
    />

  </div>
  <div v-else class="shell-empty">
    <UiCard>
      <p>Please select a character first.</p>
    </UiCard>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter, type LocationQueryRaw } from "vue-router";
import type {
  ActionPollResponse,
  Area,
  ActiveEffectEntry,
  Attributes,
  CastingAction,
  DeathEvent,
  Encounter,
  EncounterTurnResponse,
  GatheringAction,
  GroundLootPile,
  InventoryEntry,
  Location,
  Npc,
  OfflineSimulationSummary,
  ProcessingAction,
  Recipe,
  RandomEventResults,
  ResourceNode,
  Enchantment,
  EquipmentSlot,
  Spell,
  SkillId,
  TravelState,
  TravelOption,
  TurnAction
} from "@idle-rpg/shared";
import { combatXpForLevel } from "@idle-rpg/shared";
import { ICONS } from "../../../lib/icons";
import { useSessionStore } from "../../../store/session";
import { useToast } from "../../../composables/useToast";
import { formatHpWithTemp, formatNumber } from "../../../lib/format";
import {
  apiMagicCast,
  apiPauseProcessing,
  apiReadySpell,
  apiResumeMagic,
  apiResumeProcessing,
  apiSimulateOffline,
  apiStartGathering,
  apiStartProcessing,
  apiStopGathering,
  apiStopMagic,
  apiStopProcessing,
  apiUnreadySpell
} from "../infrastructure/actionsApi";
import { apiAcknowledgeDeathEvent, apiStartEncounter, apiSubmitTurn } from "../infrastructure/encountersApi";
import { apiAcknowledgeRandomEvent } from "../infrastructure/interruptsApi";
import { apiDisenchantItem, apiDropItem, apiEnchantItem, apiTakeGroundLoot, apiUseItem } from "../infrastructure/itemsApi";
import { apiLevelUp, apiSpendAp } from "../infrastructure/progressionApi";
import {
  apiEnterLocation,
  apiExitLocation,
  apiMove,
  apiTravelRest,
  apiTravelTurn,
  apiTravelWalk
} from "../infrastructure/worldApi";
import BuildingsPanel from "../../../components/panels/BuildingsPanel.vue";
import NpcsPanel from "../../../components/panels/NpcsPanel.vue";
import PlayersPanel from "../../../components/panels/PlayersPanel.vue";
import ResourcesPanel from "../../../components/panels/ResourcesPanel.vue";
import GroundLootPanel from "../../../components/panels/GroundLootPanel.vue";
import ActionDock from "../../../components/ActionDock.vue";
import { EquipmentSession } from "../../equipment";
import { InventorySession } from "../../inventory";
import { SkillsSession, applySkillProgressDelta } from "../../skills";
import { MagicSession } from "../../magic";
import { StatsSession } from "../../stats";
import { WorkshopSession } from "../../workshop";
import { BankSession, useContainersState } from "../../banking";
import { ExchangeSession } from "../../exchange";
import { ShopSession } from "../../shop";
import { LogSession } from "../../log";
import { SocialSession } from "../../economy";
import { CharacterSwitchOverlay } from "../../characters";
import { useOverlayStack } from "../../../composables/useOverlayStack";
import { useConfirm } from "../../../composables/useConfirm";
import { useTaskOrchestrator } from "../../../composables/useTaskOrchestrator";
import { useSessionSocket } from "../../../composables/useSessionSocket";
import CommandPaletteOverlay, { type CommandPaletteItem } from "./CommandPaletteOverlay.vue";
import EncounterOverlay from "./EncounterOverlay.vue";
import LevelUpOverlay from "./LevelUpOverlay.vue";
import SpendApOverlay from "./SpendApOverlay.vue";
import TravelOverlay from "./TravelOverlay.vue";
import TravelStartingOverlay from "./TravelStartingOverlay.vue";
import DeathEventOverlay from "./DeathEventOverlay.vue";
import RandomEventOverlay from "./RandomEventOverlay.vue";
import InspectOverlay from "./InspectOverlay.vue";
import OfflineSummaryOverlay, { type OfflineSummaryEntry, type OfflineSummaryLine } from "./OfflineSummaryOverlay.vue";
import UiBadge from "../../../components/ui/UiBadge.vue";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCard from "../../../components/ui/UiCard.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiIconButton from "../../../components/ui/UiIconButton.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiProgressBar from "../../../components/ui/UiProgressBar.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";
import UiSheet from "../../../components/ui/UiSheet.vue";
import UiTooltip from "../../../components/ui/UiTooltip.vue";

declare global {
  interface Window {
    __idleRpgSessionSocket?: ReturnType<typeof useSessionSocket>;
  }
}

const session = useSessionStore();
const sessionSocket = useSessionSocket();


const route = useRoute();
const router = useRouter();
const { toasts, showItemGain, showXpGain, addToast, removeToast, showCombatGood, showCombatBad } = useToast();
const coreWsStreams = ["area", "presence", "loot", "encounter", "gathering", "processing", "magic"] as const;
const wsUnsubscribers: Array<() => void> = [];
const wsHandlersReady = ref(false);

const browserOffline = ref(false);
function handleBrowserOnline() {
  browserOffline.value = false;
}
function handleBrowserOffline() {
  browserOffline.value = true;
}
function syncBrowserOfflineFromNavigator() {
  browserOffline.value = typeof navigator !== "undefined" ? !navigator.onLine : false;
}

type SessionId = "area" | "inventory" | "equipment" | "skills" | "magic" | "stats" | "workshop" | "bank" | "exchange" | "shop" | "social" | "log";

type GatheringActionState = GatheringAction & { lastPolledAt?: string };

const sessionItems: { id: SessionId; label: string; icon: string; description: string }[] = [
  { id: "area", label: "Area", icon: ICONS.sessions.area, description: "World overview and travel status." },
  { id: "inventory", label: "Inventory", icon: ICONS.sessions.inventory, description: "Items, carry, and quick actions." },
  { id: "equipment", label: "Equipment", icon: ICONS.sessions.equipment, description: "Loadout and item slots." },
  { id: "skills", label: "Skills", icon: ICONS.sessions.skills, description: "Progress and modifiers." },
  { id: "magic", label: "Magic", icon: ICONS.sessions.magic, description: "Casting state and spellbook." },
  { id: "stats", label: "Stats Snapshot", icon: ICONS.sessions.stats, description: "Attributes and effects overview." },
  { id: "workshop", label: "Workshop", icon: ICONS.sessions.workshop, description: "Processing and enchanting." },
  { id: "bank", label: "Bank", icon: ICONS.sessions.bank, description: "Storage and transfers." },
  { id: "exchange", label: "Exchange", icon: ICONS.sessions.exchange, description: "Market orders and prices." },
  { id: "shop", label: "Shop", icon: ICONS.actions.trade, description: "Trade with a selected NPC." },
  { id: "social", label: "Social", icon: ICONS.sessions.social, description: "Reputation and standing." },
  { id: "log", label: "Activity Log", icon: ICONS.sessions.log, description: "Recent events and notifications." }
];

const activeSession = ref<SessionId>("area");
const drawerOpen = ref(false);

const isMobile = ref(typeof window !== "undefined" ? window.matchMedia("(max-width: 900px)").matches : false);
const isDrawerSheet = ref(typeof window !== "undefined" ? window.matchMedia("(max-width: 900px)").matches : false);
const stickyActionSessionOpened = ref(false);
const workshopSelectedSkillId = ref<SkillId | null>(null);
const workshopSelectedRecipeId = ref<string | null>(null);

const LAYOUT_STORAGE_KEY = "idle_rpg_shell_layout_v1";
const DRAWER_WIDTH_MIN = 420;
const DRAWER_WIDTH_MAX = 640;
const DRAWER_SPINE_PX = 76;
const SHEET_SNAP_POINTS = [0.28, 0.75, 0.9];
const drawerWidth = ref<number>(480);
const drawerSheetSnap = ref<number>(1);

const shellStyle = computed(() => {
  const dockRight = isMobile.value
    ? "1.5rem"
    : `${(drawerOpen.value ? drawerWidth.value + DRAWER_SPINE_PX : DRAWER_SPINE_PX) + 24}px`;
  return {
    "--drawer-width": `${drawerWidth.value}px`,
    "--dock-right": dockRight
  };
});

const shellLoadingTitle = computed(() => {
  if (isLocationTransitioning.value) return "Loading location…";
  if (isShellLoading.value) return "Syncing world…";
  return "Loading…";
});

const shellLoadingSubtitle = computed(() => {
  if (isLocationTransitioning.value) {
    if (travel.value && travelRemainingMs.value <= 0) return "Arriving… syncing the destination area.";
    return "Updating NPCs, resources, and services.";
  }
  if (isShellLoading.value) return "Linking area data, actions, and spellbook.";
  return "Updating state.";
});

type ShellLayoutPrefs = {
  activeSession: SessionId;
  drawerOpen: boolean;
  drawerWidth: number;
  drawerSheetSnap: number;
};

function clampDrawerSheetSnap(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(SHEET_SNAP_POINTS.length - 1, Math.floor(value)));
}

function loadLayoutPrefs() {
  try {
    const raw = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Partial<ShellLayoutPrefs> | null;
    if (!parsed) return;
    if (parsed.activeSession && sessionItems.some((s) => s.id === parsed.activeSession)) {
      activeSession.value = parsed.activeSession;
    }
    if (typeof parsed.drawerOpen === "boolean") {
      drawerOpen.value = parsed.drawerOpen;
    }
    if (typeof parsed.drawerWidth === "number" && Number.isFinite(parsed.drawerWidth)) {
      drawerWidth.value = Math.max(DRAWER_WIDTH_MIN, Math.min(DRAWER_WIDTH_MAX, Math.round(parsed.drawerWidth)));
    }
    if (typeof parsed.drawerSheetSnap === "number" && Number.isFinite(parsed.drawerSheetSnap)) {
      drawerSheetSnap.value = clampDrawerSheetSnap(parsed.drawerSheetSnap);
    }
  } catch {
    // ignore
  }
}

function saveLayoutPrefs() {
  try {
    const prefs: ShellLayoutPrefs = {
      activeSession: activeSession.value,
      drawerOpen: drawerOpen.value,
      drawerWidth: drawerWidth.value,
      drawerSheetSnap: drawerSheetSnap.value
    };
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

watch(
  () => [activeSession.value, drawerOpen.value, drawerWidth.value, drawerSheetSnap.value] as const,
  () => saveLayoutPrefs()
);

const bottomNavItems = computed(() => [
  {
    id: "area" as const,
    label: "Area",
    icon: ICONS.sessions.area,
    action: () => {
      activeSession.value = "area";
      drawerOpen.value = false;
    }
  },
  {
    id: "inventory" as const,
    label: "Bag",
    icon: ICONS.sessions.inventory,
    action: () => openSession("inventory")
  },
  {
    id: "workshop" as const,
    label: "Work",
    icon: ICONS.sessions.workshop,
    action: () => openSession("workshop")
  },
  {
    id: "bank" as const,
    label: "Bank",
    icon: ICONS.sessions.bank,
    action: () => openSession("bank")
  },
  {
    id: "more" as const,
    label: "More",
    icon: ICONS.actions.search,
    action: () => openCommandPalette()
  }
]);

let mobileMql: MediaQueryList | null = null;
let drawerSheetMql: MediaQueryList | null = null;

const handleMobileMqlChange = (event: MediaQueryListEvent | MediaQueryList) => {
  const matches = "matches" in event ? event.matches : false;
  isMobile.value = matches;
};

const handleDrawerSheetMqlChange = (event: MediaQueryListEvent | MediaQueryList) => {
  const matches = "matches" in event ? event.matches : false;
  isDrawerSheet.value = matches;
  // If we're switching into sheet mode, don't force-open the "area" session drawer by default.
  if (matches && drawerOpen.value && activeSession.value === "area") drawerOpen.value = false;
};

function handleDrawerSheetSnap(index: number) {
  drawerSheetSnap.value = clampDrawerSheetSnap(index);
}

const resizingDrawer = ref(false);
let resizeStartX = 0;
let resizeStartW = 0;

function startDrawerResize(event: PointerEvent) {
  if (isDrawerSheet.value) return;
  if (!drawerOpen.value) return;
  resizingDrawer.value = true;
  resizeStartX = event.clientX;
  resizeStartW = drawerWidth.value;
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  window.addEventListener("pointermove", handleDrawerResizeMove);
  window.addEventListener("pointerup", handleDrawerResizeEnd, { once: true });
}

function handleDrawerResizeMove(event: PointerEvent) {
  if (!resizingDrawer.value) return;
  const delta = resizeStartX - event.clientX;
  const next = resizeStartW + delta;
  drawerWidth.value = Math.max(DRAWER_WIDTH_MIN, Math.min(DRAWER_WIDTH_MAX, Math.round(next)));
}

function handleDrawerResizeEnd() {
  resizingDrawer.value = false;
  window.removeEventListener("pointermove", handleDrawerResizeMove);
}

// Linear area progression: Graveyard <-> Town <-> Forest <-> City
const AREA_ADJACENCY: Record<string, string[]> = {
  graveyard: ["town"],
  town: ["graveyard", "forest"],
  forest: ["town", "city"],
  city: ["forest"]
};

const AREA_NAMES: Record<string, string> = {
  graveyard: "Graveyard",
  town: "Town",
  forest: "Forest",
  city: "City"
};

const area = ref<Area | null>(null);
const npcs = ref<Npc[]>([]);
const players = ref<{ id: string; name: string; level?: number }[]>([]);
const locations = ref<Location[]>([]);
const currentLocationId = ref<string | null>(null);
const moveTarget = ref("town");
const encounter = ref<Encounter | null>(null);
const localEncounterLog = ref<string[]>([]);
const encounterResolutionToastForId = ref<string | null>(null);
const travel = ref<TravelState | null>(null);
const travelOptions = ref<TravelOption[]>([]);
const travelRemainingMs = ref(0);
const turnSubmitting = ref(false);
let arrivalPollScheduled = false;
// Static payloads (locations/resource nodes/recipe book/enchantments) are area+location scoped.
// Track a composite key so entering/exiting buildings triggers a static refresh.
let staticScopeKey: string | null = null;
let staticLoadInFlight: string | null = null;

// Coalesce rapid-fire `area` snapshot requests (travel transitions can trigger multiple refresh sources).
let areaSnapshotInFlight = false;
let queuedAreaInclude: string[] | null = null;

function mergeAreaIncludes(a: string[], b: string[]) {
  const set = new Set<string>();
  const out: string[] = [];
  for (const v of [...a, ...b]) {
    if (!v) continue;
    if (set.has(v)) continue;
    set.add(v);
    out.push(v);
  }
  return out;
}

watch(
  () => !!encounter.value,
  (active) => {
    if (!active) return;
    drawerOpen.value = false;
  }
);

const currentLocation = computed(() => locations.value.find((l) => l.id === currentLocationId.value) || null);
const hpPercent = computed(() => {
  const maxHp = session.character?.max_hp ?? 0;
  if (!maxHp) return 0;
  return Math.round((session.character?.hp ?? 0) / maxHp * 100);
});
const manaPercent = computed(() => {
  const maxMana = session.character?.max_mana ?? 0;
  if (!maxMana) return 0;
  return Math.round((session.character?.mana ?? 0) / maxMana * 100);
});
const pendingLevelups = computed(() => session.character?.pendingLevelups ?? 0);
const apAvailable = computed(() => session.character?.apAvailable ?? 0);
const stickyLevelupToastId = ref<number | null>(null);
const stickyApToastId = ref<number | null>(null);

function updateStickyToastMessage(id: number | null, message: string) {
  if (id == null) return;
  toasts.value = toasts.value.map((toast) => (toast.id === id ? { ...toast, message } : toast));
}

watch(
  () => pendingLevelups.value,
  (count, prev) => {
    if (count <= 0) {
      if (stickyLevelupToastId.value != null) {
        removeToast(stickyLevelupToastId.value);
        stickyLevelupToastId.value = null;
      }
      return;
    }
    const message = `${formatNumber(count)} level-up${count === 1 ? "" : "s"} ready`;
    if (stickyLevelupToastId.value == null) {
      stickyLevelupToastId.value = addToast(message, "success", { sticky: true });
      return;
    }
    if (count !== prev) updateStickyToastMessage(stickyLevelupToastId.value, message);
  },
  { immediate: true }
);

watch(
  () => apAvailable.value,
  (count, prev) => {
    if (count <= 0) {
      if (stickyApToastId.value != null) {
        removeToast(stickyApToastId.value);
        stickyApToastId.value = null;
      }
      return;
    }
    const message = `${formatNumber(count)} AP unspent`;
    if (stickyApToastId.value == null) {
      stickyApToastId.value = addToast(message, "xp", { sticky: true });
      return;
    }
    if (count !== prev) updateStickyToastMessage(stickyApToastId.value, message);
  },
  { immediate: true }
);
const currentActivityLabel = computed(() => {
  if (processingAction.value) return `Processing ${processingAction.value.recipeName}`;
  if (castingAction.value) return `Casting ${castingAction.value.spellName}`;
  if (gatheringAction.value) {
    const node = resourceNodes.value.find((entry) => entry.id === gatheringAction.value?.nodeId);
    return node ? `Gathering ${node.name}` : "Gathering resources";
  }
  return "Idle — awaiting orders.";
});

const freshnessChip = computed(() => {
  if (isLocationTransitioning.value) return { variant: "blocked" as const, label: "Syncing", icon: ICONS.states.blocked };
  if (shellTasks.paused.value) return { variant: "paused" as const, label: "Paused", icon: ICONS.states.paused };
  const last = sessionSocket.lastMessageAt.value;
  if (typeof last === "number") {
    const stale = Date.now() - last > 2 * 60 * 1000;
    if (stale) return { variant: "warning" as const, label: "Stale", icon: ICONS.states.warning };
  }
  return null;
});
const connectionChip = computed(() => {
  const state = sessionSocket.connectionState.value;
  if (state === "connected") return { variant: "active" as const, label: "Connected" };
  if (state === "connecting") return { variant: "accent" as const, label: "Connecting" };
  if (state === "reconnecting") return { variant: "warning" as const, label: "Reconnecting" };
  if (state === "degraded") return { variant: "danger" as const, label: "Degraded" };
  return { variant: "muted" as const, label: "Offline" };
});
const activeSessionLabel = computed(() => sessionItems.find((item) => item.id === activeSession.value)?.label ?? "Session");
const activeSessionDescription = computed(() => sessionItems.find((item) => item.id === activeSession.value)?.description ?? "");
const overlays = useOverlayStack();
const { confirm: confirmDialog } = useConfirm();
const commandPaletteOverlayId = ref<string | null>(null);
const deathOverlayId = ref<string | null>(null);
const randomEventOverlayId = ref<string | null>(null);
const characterSwitchOverlayId = ref<string | null>(null);
const offlineSummaryOverlayId = ref<string | null>(null);
const offlineSummaryEntries = ref<OfflineSummaryEntry[]>([]);

watch(
  () => [session.token, session.character?.id] as const,
  ([token, characterId]) => {
    // Important: do not subscribe before WS snapshot handlers are registered, or we can miss the
    // initial `area` snapshot and end up in an endless loading state.
    if (!wsHandlersReady.value) return;
    if (token && characterId) {
      sessionSocket.connect({ token, characterId });
      sessionSocket.subscribe([...coreWsStreams]);
    } else {
      sessionSocket.disconnect();
    }
  },
  { immediate: true }
);

watch(
  () => session.character?.id,
  (nextId, prevId) => {
    if (!nextId || nextId === prevId) return;
    lastUsedSkillId.value = null;
    stickyActionSessionOpened.value = false;
    offlineGatheringSummaryActionId.value = null;
    offlineProcessingSummaryActionId.value = null;
    offlineCastingSummaryActionId.value = null;
    clearOfflineSummaryState();
  }
);

watch(
  () => overlays.overlays.value,
  (next) => {
    if (commandPaletteOverlayId.value && !next.some((o) => o.id === commandPaletteOverlayId.value)) {
      commandPaletteOverlayId.value = null;
    }
    if (deathOverlayId.value && !next.some((o) => o.id === deathOverlayId.value)) {
      deathOverlayId.value = null;
    }
    if (randomEventOverlayId.value && !next.some((o) => o.id === randomEventOverlayId.value)) {
      randomEventOverlayId.value = null;
    }
    if (characterSwitchOverlayId.value && !next.some((o) => o.id === characterSwitchOverlayId.value)) {
      characterSwitchOverlayId.value = null;
    }
    if (offlineSummaryOverlayId.value && !next.some((o) => o.id === offlineSummaryOverlayId.value)) {
      offlineSummaryOverlayId.value = null;
      offlineSummaryEntries.value = [];
    }
  }
);

watch(
  () => session.character?.id,
  (next, prev) => {
    if (next !== prev) {
      dismissOfflineSummaryOverlay();
    }
  }
);

	const commandPaletteCommands = computed<CommandPaletteItem[]>(() => {
	  const cmds: CommandPaletteItem[] = [
	    { id: "open-area", label: "Open Area", icon: ICONS.sessions.area, hint: "Session", keywords: ["session", "area"] },
	    { id: "open-inventory", label: "Open Inventory", icon: ICONS.sessions.inventory, hint: "Session", keywords: ["session", "items"] },
	    { id: "open-equipment", label: "Open Equipment", icon: ICONS.sessions.equipment, hint: "Session", keywords: ["session", "gear"] },
	    { id: "open-skills", label: "Open Skills", icon: ICONS.sessions.skills, hint: "Session", keywords: ["session", "xp"] },
	    { id: "open-magic", label: "Open Magic", icon: ICONS.sessions.magic, hint: "Session", keywords: ["session", "magic", "casting"] },
	    { id: "open-stats", label: "Open Stats Snapshot", icon: ICONS.sessions.stats, hint: "Session", keywords: ["session", "effects"] },
	    { id: "open-workshop", label: "Open Workshop", icon: ICONS.sessions.workshop, hint: "Session", keywords: ["session", "processing", "crafting"] },
	    { id: "open-bank", label: "Open Bank", icon: ICONS.sessions.bank, hint: "Session", keywords: ["session", "storage", "bank"] },
	    { id: "open-exchange", label: "Open Exchange", icon: ICONS.sessions.exchange, hint: "Session", keywords: ["session", "market", "exchange"] }
	  ];

	  cmds.push(
	    { id: "open-shop", label: "Open Shop", icon: ICONS.actions.trade, hint: "Session", keywords: ["session", "trade", "npc"] },
	    { id: "open-social", label: "Open Social", icon: ICONS.sessions.social, hint: "Session", keywords: ["session", "opinion"] },
	    { id: "open-log", label: "Open Activity Log", icon: ICONS.sessions.log, hint: "Session", keywords: ["session", "log", "events", "toasts"] },
	    { id: "switch-character", label: "Switch Character…", icon: ICONS.actions.switchCharacter, hint: "Character", keywords: ["character", "switch", "swap"] },
	    { id: "logout", label: "Log Out", icon: ICONS.actions.logout, hint: "System", keywords: ["logout", "exit"] }
	  );

  if (travel.value) {
    cmds.push(
      { id: "travel-walk", label: "Travel: Walk", icon: ICONS.actions.travel, hint: "Travel", keywords: ["travel", "walk"] },
      { id: "travel-rest", label: "Travel: Rest", icon: ICONS.actions.travel, hint: "Travel", keywords: ["travel", "rest"] },
      { id: "travel-turn", label: "Travel: Turn Back", icon: ICONS.actions.travel, hint: "Travel", keywords: ["travel", "turn", "back"] }
    );
  } else {
    for (const adj of adjacentAreas.value) {
      cmds.push({
        id: `travel-to:${adj.id}`,
        label: `Travel to ${adj.name}`,
        icon: ICONS.actions.travel,
        hint: "Travel",
        keywords: ["travel", "move", adj.id, adj.name]
      });
    }
  }

  if (processingAction.value) {
    cmds.push(
      processingAction.value.isPaused
        ? { id: "resume-processing", label: "Processing: Resume", icon: ICONS.states.resume, hint: "Action", keywords: ["processing", "resume"] }
        : { id: "pause-processing", label: "Processing: Pause", icon: ICONS.states.paused, hint: "Action", keywords: ["processing", "pause"] },
      { id: "cancel-processing", label: "Processing: Cancel", icon: ICONS.states.danger, hint: "Action", keywords: ["processing", "cancel", "stop"] }
    );
  }

  if (castingAction.value) {
    cmds.push(
      castingAction.value.isPaused
        ? { id: "resume-casting", label: "Casting: Resume", icon: ICONS.states.resume, hint: "Action", keywords: ["casting", "resume", "magic"] }
        : { id: "open-magic", label: "Casting: Open Magic", icon: ICONS.sessions.magic, hint: "Action", keywords: ["casting", "magic"] },
      { id: "cancel-casting", label: "Casting: Cancel", icon: ICONS.states.danger, hint: "Action", keywords: ["casting", "cancel", "stop"] }
    );
  }

  if (gatheringAction.value) {
    cmds.push({ id: "stop-gathering", label: "Gathering: Stop", icon: ICONS.states.danger, hint: "Action", keywords: ["gather", "stop"] });
  }

  return cmds;
});

function runCommandById(id: string) {
  if (id.startsWith("travel-to:")) {
    const to = id.slice("travel-to:".length);
    if (to) openTravelModal(to);
    return;
  }
  const table: Record<string, () => void> = {
    "open-area": () => openSession("area"),
    "open-inventory": () => openSession("inventory"),
    "open-equipment": () => openSession("equipment"),
    "open-skills": () => openSession("skills"),
    "open-magic": () => openSession("magic"),
    "open-stats": () => openSession("stats"),
    "open-workshop": () => openSession("workshop"),
    "open-bank": () => openSession("bank"),
    "open-exchange": () => openSession("exchange"),
    "open-shop": () => openSession("shop"),
    "open-social": () => openSession("social"),
    "open-log": () => openSession("log"),
    "switch-character": () => {
      void openCharacterSwitcher();
    },
    logout: () => {
      void logoutFromShell();
    },
    "travel-walk": () => {
      void walkTravel();
    },
    "travel-rest": () => {
      void restTravel();
    },
    "travel-turn": () => {
      void turnTravel();
    },
    "pause-processing": () => {
      void pauseProcessing();
    },
    "resume-processing": () => {
      void resumeProcessing();
    },
    "cancel-processing": () => {
      void cancelProcessing();
    },
    "resume-casting": () => {
      void resumeCasting();
    },
    "cancel-casting": () => {
      void cancelCasting();
    },
    "stop-gathering": () => {
      void stopGathering();
    }
  };
  table[id]?.();
}

const charactersRef = computed(() => session.characters);
const currentCharacterIdRef = computed(() => session.character?.id ?? null);

async function openCharacterSwitcher() {
  if (characterSwitchOverlayId.value) return;
  if (session.token && session.characters.length === 0) {
    await session.loadCharacters();
  }
  characterSwitchOverlayId.value = overlays.push({
    kind: "modal",
    component: CharacterSwitchOverlay,
    props: {
      characters: charactersRef,
      currentId: currentCharacterIdRef,
      onSelect: (id: string) => {
        void switchCharacter(id);
      }
    }
  });
}

async function switchCharacter(nextId: string) {
  if (!session.character) return;
  if (session.character.id === nextId) return;

  const leavingOnTurn = !!encounter.value && encounter.value.activeTurn === session.character.id;
  if (leavingOnTurn) {
    const ok = await confirmDialog({
      variant: "danger",
      title: "Switch characters on your turn?",
      subtitle: "The current character stays server-side; you may lose tempo if you return late.",
      body: "Switching does not end the encounter. If you return later, the server may have advanced the fight.",
      confirmLabel: "Switch anyway",
      cancelLabel: "Stay",
      closeOnScrim: true
    });
    if (!ok) return;
  }

  stopEncounterResync();
  stopTurnTimer();
  encounter.value = null;
  localEncounterLog.value = [];

  // Set up loading state BEFORE triggering the character switch watcher.
  // This ensures the automatic snapshots from set_character are processed with
  // the correct loading context (wasLoading=true) and can clear isShellLoading.
  //
  // isCharacterSwapping tells the onSnapshot handler NOT to expect static payload.
  // The server's automatic snapshot from set_character uses cached streamParams
  // which may not include "static" (or may have stale params from the previous character).
  // By setting isCharacterSwapping=true, we pass hasStatic=false to processAreaSnapshot,
  // allowing it to clear loading as soon as we have basic area data.
  isShellLoading.value = true;
  isCharacterSwapping.value = true;
  areaBootstrapError.value = null;
  ++loadAreaToken;

  session.selectCharacter(nextId);

  // Don't call loadArea() here - the watcher triggers connect() which sends
  // set_character to the server, and the server automatically sends snapshots
  // for all subscribed streams. The onSnapshot("area") handler will process
  // those snapshots and clear isShellLoading.
  // Calling loadArea() would send a duplicate resync request.

  await loadContainers({ force: true });
}

async function logoutFromShell() {
  const ok = await confirmDialog({
    variant: "danger",
    title: "Log out?",
    subtitle: "This returns you to the login screen.",
    confirmLabel: "Log out",
    cancelLabel: "Cancel",
    closeOnScrim: true
  });
  if (!ok) return;
  stopBackgroundRefresh();
  stopAreaResync();
  stopEncounterResync();
  stopProcessingResync();
  stopTurnTimer();
  encounter.value = null;
  localEncounterLog.value = [];
  session.logout();
  await router.push("/");
}

type InspectKind = "npc" | "player";

function openInspect(payload: {
  kind: InspectKind;
  title: string;
  subtitle?: string | null;
  meta?: Array<{ label: string; value: string }>;
  sections?: Array<{ title: string; body?: string | null; variant?: "default" | "coming-soon" }>;
}) {
  overlays.push({
    kind: "popover",
    component: InspectOverlay,
    props: {
      kind: payload.kind,
      title: payload.title,
      subtitle: payload.subtitle ?? null,
      meta: payload.meta ?? [],
      sections: payload.sections ?? []
    }
  });
}

function openSession(id: SessionId) {
  activeSession.value = id;
  drawerOpen.value = true;
}

function handleSessionIconClick(id: SessionId) {
  if (drawerOpen.value && activeSession.value === id) {
    drawerOpen.value = false;
    return;
  }
  openSession(id);
}

function toggleDrawer() {
  drawerOpen.value = !drawerOpen.value;
}

function collapseDrawer() {
  drawerOpen.value = false;
}

function handleWorldClick() {
  if (drawerOpen.value) {
    drawerOpen.value = false;
  }
}

function openCommandPalette() {
  if (commandPaletteOverlayId.value) return;
  commandPaletteOverlayId.value = overlays.push({
    kind: "modal",
    component: CommandPaletteOverlay,
    props: {
      commands: commandPaletteCommands.value,
      onSelect: (id: string) => runCommandById(id),
      onClose: () => {
        commandPaletteOverlayId.value = null;
      }
    }
  });
}

function closeCommandPalette() {
  if (!commandPaletteOverlayId.value) return;
  overlays.close(commandPaletteOverlayId.value);
  commandPaletteOverlayId.value = null;
}

function isTypingContext(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  const role = target.getAttribute("role");
  if (target.isContentEditable) return true;
  if (["input", "textarea", "select"].includes(tag)) return true;
  if (role && ["textbox", "combobox", "searchbox"].includes(role)) return true;
  return false;
}

const handleKeydown = (event: KeyboardEvent) => {
  if (!document.hasFocus()) return;
  const isEscape = event.key === "Escape";
  if (!isEscape && isTypingContext(event.target)) return;
  if (isEscape) {
    if (overlays.closeTop()) return;
    if (overlays.hasOverlays.value) return;
    if (encounter.value) {
      if (canDismissEncounter.value) {
        endEncounter();
      }
      return;
    }
    if (drawerOpen.value) {
      collapseDrawer();
      return;
    }
  }
  if (event.key === "/" || (event.key.toLowerCase() === "k" && (event.ctrlKey || event.metaKey))) {
    event.preventDefault();
    openCommandPalette();
    return;
  }
  const key = event.key.toLowerCase();
  const sessionKeyMap: Record<string, SessionId> = {
    i: "inventory",
    e: "equipment",
    k: "skills",
    m: "magic",
    w: "workshop",
    b: "bank",
    g: "exchange",
    c: "stats"
  };
  if (sessionKeyMap[key]) {
    openSession(sessionKeyMap[key]);
  }
};

let lastHiddenAtMs: number | null = null;

const handleVisibilityChange = () => {
  const now = Date.now();
  const isHidden = document.hidden;
  shellTasks.setPaused(isHidden);
  if (isHidden) {
    lastHiddenAtMs = now;
    return;
  }
  if (lastHiddenAtMs === null) return;
  const gapMs = now - lastHiddenAtMs;
  lastHiddenAtMs = null;
  if (gapMs >= OFFLINE_SIM_MIN_GAP_MS) {
    void runOfflineSimulation("visibility-resume", { force: true });
  }
};
const shellTasks = useTaskOrchestrator({ minTickMs: 70, maxTickMs: 2000 });
// Core world snapshot cadence (kept intentionally low-frequency; most refreshes should be event-driven).
// WS-first: keep a slow safety-net area resync to refresh regen/offline sim drift.
// Most state should refresh via change-driven WS events + stream-scoped resync.
const AREA_RESYNC_MS_AREA = 5 * 60_000;
const AREA_RESYNC_MS_OTHER = 10 * 60_000;
// Combat is time-based; we still need occasional resync to advance NPC turns / timeouts.
const ENCOUNTER_RESYNC_MS = 5000;
// Stream-scoped refresh cadences for hot UI surfaces.
// Loot is time-sensitive (public/expire windows) and should stay very fresh.
// Presence is approximate and can refresh more slowly.
// Loot is primarily event-driven; keep a slower periodic resync as a safety net.
const LOOT_RESYNC_MS = 15_000;
const PRESENCE_RESYNC_MS = 60_000;
// Character regen is minute-based; resync core vitals at 60s so +1/+1 toasts show promptly.
const VITALS_RESYNC_MS = 60_000;

let randomEventRefreshRetryTimer: number | null = null;
function scheduleRandomEventRefreshRetry(delayMs = 2100) {
  if (randomEventRefreshRetryTimer !== null) return;
  randomEventRefreshRetryTimer = window.setTimeout(() => {
    randomEventRefreshRetryTimer = null;
    // If a random event exists server-side but our immediate refresh was rate-limited,
    // retry once shortly after the server-side per-stream min interval window.
    if (randomEvent.value) return;
    void loadArea({ include: AREA_POLL_INCLUDES });
  }, Math.max(250, Math.floor(delayMs)));
}

// Turn timer (UI-only)
const turnTimeRemaining = ref<number | null>(null);
const TURN_TIMEOUT_SECONDS = 30;

const levelUpOverlayId = ref<string | null>(null);
const spendApOverlayId = ref<string | null>(null);
const travelOverlayId = ref<string | null>(null);
const travelStartingOverlayId = ref<string | null>(null);
const shopNpc = ref<Npc | null>(null);
const socialNpcStateId = ref<string | null>(null);

// Resource gathering
const resourceNodes = ref<ResourceNode[]>([]);
const gatheringAction = ref<GatheringActionState | null>(null);
const gatheringResults = ref<ActionPollResponse | null>(null);
const lastGatheringNodeDepleted = ref(false);
const isGathering = computed(() => !!gatheringAction.value);
const lastUsedSkillId = ref<SkillId | null>(null);
const offlineGatheringSummaryActionId = ref<string | null>(null);

// Processing
const processingRecipes = ref<Recipe[]>([]);
const processingAction = ref<ProcessingAction | null>(null);
const castingAction = ref<CastingAction | null>(null);
const spellbook = ref<{ learned: Spell[]; readied: Spell[]; readyLimit: number } | null>(null);
const offlineProcessingSummaryActionId = ref<string | null>(null);
const offlineCastingSummaryActionId = ref<string | null>(null);
const offlineSimulationInFlight = ref(false);
let suppressOfflineSimulation = false;

watch(
  () => route.query.open,
  async (next) => {
    if (next !== "crafting") return;
    const query = { ...(route.query as LocationQueryRaw) };
    delete (query as any).open;
    await router.replace({ path: route.path, query });
  },
  { immediate: true }
);
const carryState = ref<any>(null);
const containers = useContainersState();
const containersState = containers.state;
const enchantments = ref<Enchantment[]>([]);
const activeProcessingRecipe = computed(() => {
  if (!processingAction.value) return null;
  return processingRecipes.value.find((r) => r.id === processingAction.value?.recipeId) || null;
});

const resolveActionKey = (action?: { id?: string; startedAt?: string } | null) =>
  action?.id ?? action?.startedAt ?? null;

watch(
  () => resolveActionKey(gatheringAction.value),
  () => {
    offlineGatheringSummaryActionId.value = null;
  }
);

watch(
  () => resolveActionKey(processingAction.value),
  () => {
    offlineProcessingSummaryActionId.value = null;
  }
);

watch(
  () => resolveActionKey(castingAction.value),
  () => {
    offlineCastingSummaryActionId.value = null;
  }
);

const STATION_LABEL_BY_LOCATION_TYPE: Record<string, string> = {
  smithy: "Smithy",
  hut: "Hearth",
  workshop: "Workshop Bench",
  tower: "Arcane Altar"
};

const currentStationLabel = computed(() => {
  const type = (currentLocation.value?.type || "").toString();
  return STATION_LABEL_BY_LOCATION_TYPE[type] || null;
});
const bankAvailableHere = computed(() => {
  const location = currentLocation.value;
  if (!location) return false;
  if ((location.services || []).includes("bank")) return true;
  return location.type === "bank";
});
const bankBlockedReason = computed(() =>
  bankAvailableHere.value ? null : "You need to be at a bank to access storage."
);
const exchangeBlockedReason = computed(() =>
  bankAvailableHere.value ? null : "The Exchange terminal is only available at a bank."
);
const bankTab = ref<"bank" | "containers">("bank");
const hasSpineQuickActions = computed(() => !!processingAction.value || !!castingAction.value || !!gatheringAction.value);
const processingProgressBaseMs = ref(0);
const processingProgressBaseAtMs = ref(Date.now());
const processingProgressLastActionId = ref<string>("");
const processingProgressLastRemaining = ref<number>(0);
const processingProgressLastPaused = ref<boolean>(false);
const processingUiNowMs = ref(Date.now());

watch(
  () =>
    [
      processingAction.value?.id,
      processingAction.value?.remaining,
      processingAction.value?.progressMs,
      processingAction.value?.isPaused
    ] as const,
  ([id]) => {
    const action = processingAction.value;
    if (!id || !action) {
      processingProgressBaseMs.value = 0;
      processingProgressBaseAtMs.value = Date.now();
      processingProgressLastActionId.value = "";
      processingProgressLastRemaining.value = 0;
      processingProgressLastPaused.value = false;
      return;
    }

    const denom = Math.max(1, Number(activeProcessingRecipe.value?.durationMs || action.durationMs || 1));
    const progress = Math.max(0, Math.min(denom, Math.floor(Number(action.progressMs || 0))));
    const remaining = Math.max(0, Math.floor(Number(action.remaining || 0)));
    const paused = !!action.isPaused;

    const isNewAction = id !== processingProgressLastActionId.value;
    const remainingChanged = remaining !== processingProgressLastRemaining.value;
    const pauseToggled = paused !== processingProgressLastPaused.value;

    if (isNewAction || remainingChanged || pauseToggled) {
      processingProgressBaseMs.value = progress;
    } else {
      processingProgressBaseMs.value = Math.max(processingProgressBaseMs.value, progress);
    }

    processingProgressBaseAtMs.value = Date.now();
    processingProgressLastActionId.value = id;
    processingProgressLastRemaining.value = remaining;
    processingProgressLastPaused.value = paused;
  },
  { immediate: true }
);

const processingProgressMsLive = computed(() => {
  const action = processingAction.value;
  if (!action) return 0;
  const denom = Math.max(1, Number(activeProcessingRecipe.value?.durationMs || action.durationMs || 1));
  const base = Math.max(0, Math.min(denom, processingProgressBaseMs.value));
  if (action.isPaused) return base;
  const elapsed = Math.max(0, processingUiNowMs.value - processingProgressBaseAtMs.value);
  return Math.min(denom, base + elapsed);
});

const processingProgressPercent = computed(() => {
  const action = processingAction.value;
  if (!action) return 0;
  const denom = Number(activeProcessingRecipe.value?.durationMs || action.durationMs || 0);
  if (!Number.isFinite(denom) || denom <= 0) return 0;
  const pct = (Math.max(0, Math.min(denom, processingProgressMsLive.value)) / denom) * 100;
  return Math.min(100, Math.max(0, pct));
});

function formatProcessingRemaining(remaining: number): string {
  if (remaining <= 0) return "Completed";
  if (remaining === 1) return "1 unit";
  return `${remaining} units`;
}

// Ground loot
const groundLoot = ref<GroundLootPile[]>([]);

// Death UX
const deathEvent = ref<DeathEvent | null>(null);


// Random Event UX
const randomEvent = ref<RandomEventResults | null>(null);
const lastRandomEventId = ref<string | null>(null);
const effectSums = ref<{ attr: Partial<Record<keyof Attributes, number>>; skill: Partial<Record<SkillId, number>> } | null>(null);
const activeEffects = ref<ActiveEffectEntry[]>([]);

function formatEffectLabel(effect: ActiveEffectEntry): string {
  const magnitude = Math.floor(Number(effect.magnitude || 0));
  const sign = magnitude >= 0 ? `+${magnitude}` : `${magnitude}`;
  if (effect.kind === 'attr') {
    return `${String(effect.key).toUpperCase()} ${sign}`;
  }
  const raw = String(effect.key || '');
  const label = raw ? raw.slice(0, 1).toUpperCase() + raw.slice(1) : 'Skill';
  return `${label} ${sign}`;
}

const usingItemId = ref<string | null>(null);

const adjacentAreas = computed(() => {
  const currentAreaId = session.character?.areaId;
  if (!currentAreaId) return [];
  const adjacent = AREA_ADJACENCY[currentAreaId] || [];
  return adjacent.map(id => ({ id, name: AREA_NAMES[id] || id }));
});

const isInBuilding = computed(() => currentLocationId.value !== null);
const isLocationTransitioning = ref(false);
const isShellLoading = ref(true);
const isCharacterSwapping = ref(false);
const areaBootstrapError = ref<string | null>(null);
let loadAreaToken = 0;

let areaBootstrapInFlight = false;
let areaBootstrapRetryTimer: ReturnType<typeof setTimeout> | null = null;
let areaBootstrapFallbackTimer: ReturnType<typeof setTimeout> | null = null;

function clearAreaBootstrapTimers() {
  if (areaBootstrapRetryTimer !== null) {
    clearTimeout(areaBootstrapRetryTimer);
    areaBootstrapRetryTimer = null;
  }
  if (areaBootstrapFallbackTimer !== null) {
    clearTimeout(areaBootstrapFallbackTimer);
    areaBootstrapFallbackTimer = null;
  }
}

function parseRetryAfterMs(message: string): number | null {
  const match = /Retry after ~(\d+)ms/i.exec(message);
  if (!match) return null;
  const value = Number(match[1]);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.floor(value);
}

function scheduleAreaBootstrapRetry(err: Error) {
  if (areaBootstrapRetryTimer !== null) return;
  if (!session.token || !session.character) return;
  if (!isShellLoading.value) return;

  const retryAfterMs = parseRetryAfterMs(err.message);
  const baseDelay = retryAfterMs ?? 1200;
  const delay = Math.max(300, Math.min(12_000, Math.floor(baseDelay)));

  areaBootstrapRetryTimer = setTimeout(() => {
    areaBootstrapRetryTimer = null;
    if (!session.token || !session.character) return;
    if (!isShellLoading.value) return;
    if (areaBootstrapInFlight) return;
    void loadArea({ showLoading: true, include: AREA_POLL_INCLUDES_WITH_STATIC });
  }, delay);
}

function scheduleInitialAreaBootstrapFallback() {
  if (areaBootstrapFallbackTimer !== null) return;
  // Give the WS subscribe snapshot a chance to arrive first; only fallback to an explicit snapshot request if we're still loading.
  areaBootstrapFallbackTimer = setTimeout(() => {
    areaBootstrapFallbackTimer = null;
    if (!session.token || !session.character) return;
    if (!isShellLoading.value) return;
    if (!sessionSocket.isConnected.value) {
      // If we're not connected yet, re-schedule once the connection is likely established.
      scheduleInitialAreaBootstrapFallback();
      return;
    }
    if (areaBootstrapInFlight) return;
    void loadArea({ showLoading: true, include: AREA_POLL_INCLUDES_WITH_STATIC });
  }, 1400);
}

function retryAreaBootstrap() {
  clearAreaBootstrapTimers();
  areaBootstrapError.value = null;
  if (!session.token || !session.character) return;
  void loadArea({ showLoading: true, include: AREA_POLL_INCLUDES_WITH_STATIC });
}

const inCombat = computed(() => {
  // Player is in combat if there's an active encounter with an active turn
  return !!encounter.value && !!encounter.value.activeTurn;
});

const showBuildingsPanel = computed(
  () => isLocationTransitioning.value || inCombat.value || locations.value.length > 0
);
const showNpcsPanel = computed(() => npcs.value.length > 0);
const showPlayersPanel = computed(() => players.value.length > 0);
const showGroundLootPanel = computed(() => groundLoot.value.length > 0);
const showResourcesPanel = computed(() => resourceNodes.value.length > 0 || isGathering.value);

const consumableItems = computed(() =>
  (session.character?.inventory || []).filter((entry) => {
    if (entry.item?.type === "consumable") return true;
    if (entry.item?.type === "spell-scroll") return Boolean((entry.item as any).scrollSpellId);
    return false;
  })
);

const equippedWeapon = computed(() => session.character?.equipment?.main_hand || null);
const equippedWeaponTags = computed(() => equippedWeapon.value?.item?.tags || []);
const equippedQuiverInstanceId = computed(() => session.character?.equipment?.back?.instanceIds?.[0] || null);
const quiverStates = computed(() => (containersState.value?.containers || []).filter((c) => c.kind === "quiver"));
const equippedQuiver = computed(() =>
  quiverStates.value.find((c) => c.instanceId === equippedQuiverInstanceId.value) || null
);
const equippedQuiverAmmo = computed(() => equippedQuiver.value?.contents?.[0] || null);
const equippedQuiverCapacity = computed(() => equippedQuiver.value?.maxQuantity ?? 0);
const quiverAmmoQuantity = computed(() => equippedQuiverAmmo.value?.quantity ?? 0);
const quiverAmmoName = computed(() => equippedQuiverAmmo.value?.item?.name || equippedQuiverAmmo.value?.itemId || null);
const quiverCapacityLeft = computed(() => Math.max(0, equippedQuiverCapacity.value - quiverAmmoQuantity.value));
const isRangedWeapon = computed(() => equippedWeaponTags.value.includes("ranged"));
const reloadOptions = computed(() => {
  const currentAmmoId = equippedQuiverAmmo.value?.itemId;
  const options =
    (session.character?.inventory || [])
      .filter((entry) => !entry.instanceIds && entry.quantity > 0 && entry.item?.tags?.includes("ammo"))
      .map((entry) => ({
        itemId: entry.itemId,
        quantity: entry.quantity,
        label: `${entry.item?.name || entry.itemId} (${entry.quantity}×)`
      })) || [];
  return options.sort((a, b) => {
    if (currentAmmoId && a.itemId === currentAmmoId) return -1;
    if (currentAmmoId && b.itemId === currentAmmoId) return 1;
    return (b.quantity || 0) - (a.quantity || 0);
  });
});
const hotSwapOptions = computed(() =>
  quiverStates.value
    .filter((q) => q.instanceId !== equippedQuiverInstanceId.value && (q.contents?.[0]?.quantity ?? 0) > 0)
    .map((q) => {
      const ammo = q.contents?.[0];
      const ammoQty = ammo?.quantity ?? 0;
      const ammoName = ammo?.item?.name || ammo?.itemId || "Arrows";
      const capacity = q.maxQuantity ?? q.capacitySlots ?? 0;
      return {
        instanceId: q.instanceId,
        label: q.itemName || "Quiver",
        status: `${ammoQty}× ${ammoName} (${capacity || "?"} cap)`
      };
    })
);
const reloadDisabledReason = computed(() => {
  if (!equippedQuiver.value) return "Equip a quiver to reload.";
  if (!reloadOptions.value.length) return "No loose arrows in your inventory.";
  if (quiverCapacityLeft.value <= 0) return "Quiver is already full.";
  const currentAmmoId = equippedQuiverAmmo.value?.itemId;
  if (currentAmmoId && !reloadOptions.value.some((o) => o.itemId === currentAmmoId)) {
    return "Your loose arrows don't match the equipped quiver's ammo type.";
  }
  return null;
});
const canReload = computed(() => !reloadDisabledReason.value);
const combatLoadout = computed(() => {
  const weaponName = equippedWeapon.value?.item?.name || "Unarmed";
  const weaponDetail = equippedWeapon.value?.item?.damageDice
    ? `${equippedWeapon.value.item.damageDice}${equippedWeapon.value.item.damageBonus ? ` +${equippedWeapon.value.item.damageBonus}` : ""}`
    : null;
  const weaponStyle = equippedWeaponTags.value.includes("ranged")
    ? "Ranged"
    : equippedWeaponTags.value.includes("magic")
    ? "Magic"
    : equippedWeapon.value
    ? "Melee"
    : "Unarmed";
  const quiverLabel = equippedQuiver.value?.itemName || (equippedQuiverInstanceId.value ? "Quiver" : null);
  const capacity = equippedQuiverCapacity.value || 0;
  const quiverDetail = equippedQuiver.value
    ? equippedQuiverAmmo.value
      ? `${quiverAmmoQuantity.value}× ${quiverAmmoName.value || "Arrows"}`
      : "Empty quiver"
    : isRangedWeapon.value
    ? "Equip a quiver to fire arrows."
    : null;
  const quiverStatus = equippedQuiver.value ? `${quiverAmmoQuantity.value}/${capacity || "?"}` : null;
  const quiverWarning = !isRangedWeapon.value
    ? null
    : !equippedQuiver.value
    ? "You have a ranged weapon equipped but no quiver."
    : quiverAmmoQuantity.value <= 0
    ? "Quiver is empty — attacks will be unarmed."
    : null;
  return {
    weaponLabel: weaponName,
    weaponDetail,
    weaponStyle,
    quiverLabel,
    quiverStatus,
    quiverDetail,
    quiverWarning,
    quiverEmpty: quiverAmmoQuantity.value <= 0,
    quiverCapacity: capacity || null,
    quiverFillPercent: capacity > 0 ? Math.min(100, Math.round((quiverAmmoQuantity.value / capacity) * 100)) : 0,
    arrowName: quiverAmmoName.value
  };
});

const bonusActionUsed = computed(() => {
  if (!encounter.value || !session.character?.id || !encounter.value.turnStartedAt) return false;
  return (encounter.value.bonusActions || {})[session.character.id] === encounter.value.turnStartedAt;
});

const canDismissEncounter = computed(() => !!encounter.value && !encounter.value.activeTurn);

const hotSwapDisabledReason = computed(() => {
  if (!hotSwapOptions.value.length) return "No loaded quivers in inventory.";
  if (bonusActionUsed.value) return "Bonus action already used this turn.";
  return null;
});
const canHotSwap = computed(() => !hotSwapDisabledReason.value);

const canUseConsumable = computed(() => {
  if (!encounter.value) return true;
  if (!session.character) return false;
  if (encounter.value.activeTurn !== session.character.id || !encounter.value.turnStartedAt) return false;
  return !bonusActionUsed.value;
});

const consumableDisabledReason = computed(() => {
  if (!encounter.value) return null;
  if (!session.character) return "";
  if (encounter.value.activeTurn !== session.character.id || !encounter.value.turnStartedAt) {
    return "Wait for your turn to use a consumable.";
  }
  if (bonusActionUsed.value) return "Bonus action already used this turn.";
  return null;
});

const playerIdRef = computed(() => session.character?.id ?? "");
const readiedSpellsRef = computed(() => spellbook.value?.readied ?? []);

watch(
  () => encounter.value,
  (next, prev) => {
    if (next && !prev) collapseDrawer();
  }
);

watch(
  () => deathEvent.value,
  (next) => {
    const shouldShow = !!next && !next.acknowledgedAt;
    if (shouldShow && !deathOverlayId.value) {
      if (randomEventOverlayId.value) {
        overlays.close(randomEventOverlayId.value, "api");
        randomEventOverlayId.value = null;
      }
      deathOverlayId.value = overlays.push({
        kind: "blocking",
        closeOnEsc: true,
        component: DeathEventOverlay,
        onClose: (reason) => {
          if (reason !== "esc") return;
          void acknowledgeDeathAndClose();
        },
        props: {
          deathEvent: next!,
          onAcknowledge: acknowledgeDeathAndClose
        }
      });
      return;
    }
    if (!shouldShow && deathOverlayId.value) {
      overlays.close(deathOverlayId.value, "api");
      deathOverlayId.value = null;
    }
  }
);

watch(
  () => [randomEvent.value, deathEvent.value, encounter.value] as const,
  ([event, death, activeEncounter]) => {
    const shouldShow = !!event && !(death && !death.acknowledgedAt) && !activeEncounter;
    if (shouldShow && !randomEventOverlayId.value) {
      randomEventOverlayId.value = overlays.push({
        kind: "blocking",
        closeOnEsc: true,
        component: RandomEventOverlay,
        onClose: (reason) => {
          if (reason !== "esc") return;
          void acknowledgeRandomEventAndClose();
        },
        props: {
          event: event!,
          onAcknowledge: acknowledgeRandomEventAndClose
        }
      });
      return;
    }
    if (!shouldShow && randomEventOverlayId.value) {
      overlays.close(randomEventOverlayId.value, "api");
      randomEventOverlayId.value = null;
    }
  }
);

watch(
  () => containersState.value,
  (next) => {
    if (!next) return;
    if (next.carry !== undefined) carryState.value = (next as any).carry ?? null;
  },
  { immediate: true }
);

async function loadContainers(options?: { force?: boolean }) {
  await containers.refresh({ force: options?.force ?? false });
}

async function openContainers() {
  bankTab.value = "containers";
  openSession("bank");
}

// (Containers management now lives in the BankSession; the shell only needs read access for ranged/quiver UX.)

function openShop(npc: Npc) {
  shopNpc.value = npc;
  openSession("shop");
}

function handleSocialHint(npc: Npc) {
  socialNpcStateId.value = npc.id;
  openSession('social');
}


function handleInspect(npc: Npc) {
  const locationLabel = npc.locationId ? (locations.value.find((l) => l.id === npc.locationId)?.name ?? npc.locationId) : "Outside";
  openInspect({
    kind: "npc",
    title: npc.name,
    subtitle: `${npc.role} • ${npc.disposition}`,
    meta: [
      { label: "NPC State", value: npc.id },
      { label: "Area", value: npc.areaId },
      { label: "Location", value: locationLabel }
    ],
    sections: [
      {
        title: "Overview",
        body: `Disposition: ${npc.disposition}. Role: ${npc.role}.`,
        variant: "default"
      },
      {
        title: "Services",
        body: npc.hasShop
          ? "Trade is available. Use the Trade action to open the Shop session in the drawer."
          : "No shop services detected.",
        variant: "default"
      },
      {
        title: "Reputation & Social",
        body: "Use the Social session to see opinion, bribe cost, and social actions for this NPC.",
        variant: "default"
      },
      { title: "Loot & Drops", body: "Loot bands and rarity will appear here once compendium/loot UI lands.", variant: "coming-soon" }
    ]
  });
}

function handleSteal(npc: Npc) {
  addToast(`Stealing from ${npc.name} isn't implemented yet.`, "warning");
}

// Optimization (Cloudflare Workers / D1):
// - `static` payloads (recipes/nodes/etc.) are effectively deployment-scoped and expensive to build.
//   Keep them out of the steady-state resync loop; fetch them only on bootstrap and area transitions.
// - High-frequency action streams (gathering/processing) should not include full inventory snapshots every tick.
const AREA_POLL_INCLUDES = [
  "core",
  "world",
  "actions",
  "effects",
  "inventory",
  "equipment",
  "carry",
  "events",
  "travel",
  "progress"
];
const AREA_POLL_INCLUDES_WITH_STATIC = [...AREA_POLL_INCLUDES, "static"];
// When we only need to refresh world/overlay state (random events, action states), avoid inventory/equipment rebuilds.
const AREA_POLL_INCLUDES_LIGHT = ["core", "world", "actions", "effects", "events", "travel", "progress"];

function isCurrentCharacter(expectedId: string | null | undefined) {
  return !!expectedId && session.character?.id === expectedId;
}

async function processAreaSnapshot(data: any, options?: { hasStatic?: boolean; showLoading?: boolean; loadAreaToken?: number }) {
  const hasStatic = options?.hasStatic ?? false;
  const showLoading = options?.showLoading ?? false;
  const token = options?.loadAreaToken ?? 0;

  if (!session.character) return;

  const oldHp = session.character.hp;
  const oldMana = session.character.mana;
  const prevTravel = travel.value;
  const prevAreaId = area.value?.id ?? null;
  const hasStaticPayload =
    data.locations !== undefined ||
    data.resourceNodes !== undefined ||
    data.processingRecipes !== undefined ||
    data.enchantments !== undefined;

  const areaId = data.area?.id ?? null;
  const incomingLocationId =
    data.currentLocationId !== undefined ? ((data.currentLocationId as string | null) ?? null) : currentLocationId.value;
  const nextStaticScopeKey = areaId ? `${areaId}|${incomingLocationId ?? "none"}` : null;

  // Travel/building transitions can temporarily deliver core-only snapshots (vitals, travel countdown).
  // If the area changed but we don't yet have the static world payload (locations/resources), schedule a single follow-up.
  if (data.area && prevAreaId && data.area.id !== prevAreaId && !hasStaticPayload && nextStaticScopeKey) {
    if (staticLoadInFlight !== nextStaticScopeKey) {
      staticLoadInFlight = nextStaticScopeKey;
      void loadArea({ include: AREA_POLL_INCLUDES_WITH_STATIC });
    }
  }
  if (nextStaticScopeKey && hasStaticPayload) {
    staticScopeKey = nextStaticScopeKey;
    staticLoadInFlight = null;
  }
  if (nextStaticScopeKey && staticScopeKey !== nextStaticScopeKey && !hasStaticPayload && staticLoadInFlight !== nextStaticScopeKey) {
    staticLoadInFlight = nextStaticScopeKey;
    void loadArea({ include: AREA_POLL_INCLUDES_WITH_STATIC });
  }
  if (data.area) {
    area.value = data.area;
  }
  if (data.locations) {
    locations.value = data.locations as Location[];
  }
  if (data.currentLocationId !== undefined) {
    currentLocationId.value = (data.currentLocationId as string | null) ?? null;
  }
  if (data.travel !== undefined) {
    travel.value = (data.travel as TravelState | null) ?? null;
  }
  if (data.travelOptions) {
    travelOptions.value = (data.travelOptions as TravelOption[] | undefined) ?? [];
  }
  travelRemainingMs.value = travel.value?.remainingMs ?? 0;
  arrivalPollScheduled = false;
  maybeCloseTravelStartingOverlay();

  // Arrival moment: we were traveling and now we aren't, and the area transitioned.
  if (prevTravel && !travel.value) {
    const arrivedAreaName = AREA_NAMES[prevTravel.toAreaId] || prevTravel.toAreaId;
    if (area.value && prevAreaId !== area.value.id) {
      addToast(`Arrived in ${arrivedAreaName}.`, "info");
    }
    if (isLocationTransitioning.value) {
      isLocationTransitioning.value = false;
    }
  }

  // If we're not traveling, keep the move dropdown sane for the current hub.
  if (!travel.value) {
    const currentAreaId = area.value?.id;
    if (!currentAreaId) return;
    const adjacent = AREA_ADJACENCY[currentAreaId] || [];
    const validIds = new Set(adjacent);
    if (!validIds.has(moveTarget.value)) {
      moveTarget.value = adjacent[0] || currentAreaId;
    }
  }

  if (session.character) {
    // Server is authoritative for current area (important for respawn-on-death).
    if (data.area) {
      session.character.areaId = data.area.id as any;
    }
    session.character.locationId = currentLocationId.value;
    if (data.inventory) {
      session.character.inventory = data.inventory as InventoryEntry[];
    }
    if (typeof data.gold === "number") {
      session.character.gold = data.gold;
    }
    if (data.equipment) {
      session.character.equipment = data.equipment as any;
    }
    if (data.carry !== undefined) {
      const carry = (data as any).carry ?? null;
      carryState.value = carry;
      (session.character as any).carry = carry;
    }

    // Check for HP regeneration and show toast
    if (typeof data.hp === "number") {
      const newHp = data.hp;
      if (newHp > oldHp) {
        const healed = newHp - oldHp;
        showCombatGood(`+${healed} HP regenerated`);
      }
      session.character.hp = newHp;
    }
    if (typeof data.mana === "number") {
      const newMana = data.mana;
      if (newMana > oldMana) {
        const restored = newMana - oldMana;
        showCombatGood(`+${restored} mana regenerated`);
      }
      session.character.mana = newMana;
    }
    if (data.tempHp !== undefined) {
      session.character.temp_hp = data.tempHp ?? session.character.temp_hp ?? 0;
    }
    if (data.tempHpExpiresAt !== undefined) {
      session.character.temp_hp_expires_at = (data.tempHpExpiresAt as any) ?? null;
    }
    if (data.maxHpDebuff !== undefined) {
      session.character.max_hp_debuff = data.maxHpDebuff ?? 0;
    }
    if (data.maxHpDebuffExpiresAt !== undefined) {
      session.character.max_hp_debuff_expires_at = (data.maxHpDebuffExpiresAt as any) ?? null;
    }
    if (data.maxManaDebuff !== undefined) {
      session.character.max_mana_debuff = data.maxManaDebuff ?? 0;
    }
    if (data.maxManaDebuffExpiresAt !== undefined) {
      session.character.max_mana_debuff_expires_at = (data.maxManaDebuffExpiresAt as any) ?? null;
    }

    // Update skill progress and level-up data
    if (data.skillProgress) {
      session.character.skillProgress = data.skillProgress;
    }
    if (data.pendingLevelups !== undefined) {
      session.character.pendingLevelups = data.pendingLevelups;
    }
    if (data.apAvailable !== undefined) {
      session.character.apAvailable = data.apAvailable;
    }
  }

  if (data.deathEvent !== undefined) {
    deathEvent.value = (data.deathEvent as DeathEvent | null) ?? null;
  }

  if (data.randomEvent !== undefined) {
    randomEvent.value = (data.randomEvent as RandomEventResults | null) ?? null;
  }
  if (randomEvent.value) {
    if (randomEvent.value.id !== lastRandomEventId.value) {
      lastRandomEventId.value = randomEvent.value.id;
      if (randomEvent.value.contextType === "processing" && processingAction.value?.isPaused) {
        addToast("Processing paused: random event interruption.", "warning");
      }
      if (randomEvent.value.contextType === "processing" && castingAction.value?.isPaused) {
        addToast("Casting paused: random event interruption.", "warning");
      }
    }
  } else {
    lastRandomEventId.value = null;
  }

  if (data.groundLoot) {
    groundLoot.value = (data.groundLoot as GroundLootPile[]) ?? [];
  }
  if (data.npcs) {
    npcs.value = data.npcs as Npc[];
  }
  if (data.players) {
    players.value = data.players as { id: string; name: string; level?: number }[];
  }
  if (data.resourceNodes) {
    resourceNodes.value = data.resourceNodes as ResourceNode[];
  }
  if (data.currentGatheringAction !== undefined) {
    const nextGathering = (data.currentGatheringAction as GatheringAction | null) ?? null;
    const priorPolledAt = gatheringAction.value?.lastPolledAt;
    gatheringAction.value = nextGathering ? { ...nextGathering, lastPolledAt: priorPolledAt } : null;
  }
  if (data.effectSums !== undefined) {
    effectSums.value = (data.effectSums as any) ?? null;
  }
  if (data.activeEffects !== undefined) {
    activeEffects.value = (data.activeEffects as ActiveEffectEntry[] | undefined) ?? [];
  }
  if (data.processingRecipes !== undefined) {
    processingRecipes.value = (data.processingRecipes as Recipe[] | undefined) ?? [];
  }
  if (data.currentProcessingAction !== undefined) {
    processingAction.value = (data.currentProcessingAction as ProcessingAction | undefined) ?? null;
  }
  if (data.currentCastingAction !== undefined) {
    castingAction.value = (data.currentCastingAction as CastingAction | undefined) ?? null;
  }
  if ((data as any).spellbook !== undefined) {
    spellbook.value = ((data as any).spellbook as any) ?? null;
  }
  if (data.enchantments !== undefined) {
    enchantments.value = (data.enchantments as Enchantment[] | undefined) ?? [];
  }

  // If we have an active encounter (e.g., app relaunched mid-fight), reopen it.
  const activeEncounterId = (data.activeEncounterId as string | null) ?? null;
  if (activeEncounterId) {
    if (!encounter.value || encounter.value.id !== activeEncounterId) {
      let snapshot;
      try {
        snapshot = await sessionSocket.requestSnapshot("encounter");
      } catch {
        return;
      }
      const payload = snapshot.data;
      if (payload.encounter) {
        encounter.value = payload.encounter as Encounter;
        localEncounterLog.value = [];
        await loadContainers({ force: true });
        startEncounterResync();
        startTurnTimer();
      }
    }
  } else {
    // If backend says we're not in an encounter anymore, stop live resync/timer,
    // but keep the modal open until the player dismisses it (so they can read the log).
    if (encounter.value) {
      stopEncounterResync();
      stopTurnTimer();
    }
  }

  // Only clear the loading overlay once we have enough payload to render a non-empty world.
  // While bootstrapping we can receive "core-only" area snapshots (e.g. vitals) that should not
  // drop the player into an empty world before the static/world payload arrives.
  const canFinishLoading =
    showLoading &&
    loadAreaToken === token &&
    (hasStatic ? hasStaticPayload : data.area !== undefined);
  if (canFinishLoading) {
    isShellLoading.value = false;
  }

  // Offline simulation can be slow on cold starts or flaky networks; don't block initial render on it.
  if (showLoading && !suppressOfflineSimulation) {
    void runOfflineSimulation("load-area");
  }
}

async function loadArea(options?: { include?: string[]; showLoading?: boolean }) {
  const showLoading = !!options?.showLoading;
  const token = showLoading ? ++loadAreaToken : 0;
  const include = options?.include ?? AREA_POLL_INCLUDES;
  const hasStatic = include.includes("static");
  if (!showLoading && areaSnapshotInFlight) {
    queuedAreaInclude = queuedAreaInclude ? mergeAreaIncludes(queuedAreaInclude, include) : include;
    return;
  }
  if (showLoading) {
    isShellLoading.value = true;
    areaBootstrapError.value = null;
    areaBootstrapInFlight = true;
  }
  if (!session.token || !session.character) {
    if (showLoading && loadAreaToken === token) {
      isShellLoading.value = false;
      areaBootstrapInFlight = false;
    }
    return;
  }

  const requestedCharacterId = session.character.id;

  try {
    if (!showLoading) areaSnapshotInFlight = true;
    let snapshot;
    try {
      snapshot = await sessionSocket.requestSnapshot("area", { include }, showLoading ? { timeoutMs: 15000 } : undefined);
    } catch (err) {
      if (showLoading && loadAreaToken === token && isShellLoading.value) {
        const message = err instanceof Error ? err.message : "Failed to load area snapshot";
        areaBootstrapError.value = message;
        if (err instanceof Error) {
          scheduleAreaBootstrapRetry(err);
        }
        // Keep the loading overlay up; the retry loop + button will handle recovery.
        return;
      }
      if (err instanceof Error && !showLoading) addToast(err.message, "warning");
      return;
    }
    if (!isCurrentCharacter(requestedCharacterId)) return;
    const data = snapshot.data;

    // Process the snapshot using the shared processing function
    await processAreaSnapshot(data, { hasStatic, showLoading, loadAreaToken: token });
    if (showLoading && loadAreaToken === token) {
      clearAreaBootstrapTimers();
      areaBootstrapError.value = null;
    }
  } catch (err) {
    if (showLoading && loadAreaToken === token) {
      isShellLoading.value = false;
      clearAreaBootstrapTimers();
      areaBootstrapError.value = null;
    }
    throw err;
  } finally {
    if (!showLoading) {
      areaSnapshotInFlight = false;
      if (queuedAreaInclude) {
        const nextInclude = queuedAreaInclude;
        queuedAreaInclude = null;
        // Best-effort: coalesced follow-up to pick up static/world payload if requested while in-flight.
        void loadArea({ include: nextInclude });
      }
    }
    if (showLoading) {
      areaBootstrapInFlight = false;
    }
  }
}

async function resyncVitals() {
  if (!session.token || !session.character) return;
  // We intentionally fetch only `core` so we can apply HP/Mana deltas (regen) without
  // touching travel countdown state (which is managed by the travel timer task).
  const requestedCharacterId = session.character.id;
  let snapshot;
  try {
    snapshot = await sessionSocket.requestSnapshot("area", { include: ["core"] });
  } catch {
    return;
  }
  if (!isCurrentCharacter(requestedCharacterId)) return;
  const data = snapshot.data as any;
  if (!session.character) return;

  // Regen toast UX: show per-minute deltas rather than batched deltas at the slow area cadence.
  const oldHp = session.character.hp;
  const oldMana = session.character.mana;

  if (typeof data.hp === "number") {
    const newHp = data.hp;
    if (newHp > oldHp) {
      const healed = newHp - oldHp;
      showCombatGood(`+${healed} HP regenerated`);
    }
    session.character.hp = newHp;
  }

  if (typeof data.mana === "number") {
    const newMana = data.mana;
    if (newMana > oldMana) {
      const restored = newMana - oldMana;
      showCombatGood(`+${restored} mana regenerated`);
    }
    session.character.mana = newMana;
  }
}

async function resyncLoot() {
  if (!session.token || !session.character) return;
  const requestedCharacterId = session.character.id;
  let snapshot;
  try {
    snapshot = await sessionSocket.requestSnapshot("loot");
  } catch {
    return;
  }
  if (!isCurrentCharacter(requestedCharacterId)) return;
  const { areaId, groundLoot: nextLoot, deathEvent: nextDeath } = snapshot.data as any;
  const currentAreaId = (area.value?.id ?? session.character?.areaId ?? null) as any;
  if (areaId && currentAreaId && areaId !== currentAreaId) return;
  groundLoot.value = (nextLoot as GroundLootPile[]) ?? [];
  deathEvent.value = (nextDeath as DeathEvent | null) ?? null;
}

async function resyncPresence() {
  if (!session.token || !session.character) return;
  const requestedCharacterId = session.character.id;
  let snapshot;
  try {
    snapshot = await sessionSocket.requestSnapshot("presence");
  } catch {
    return;
  }
  if (!isCurrentCharacter(requestedCharacterId)) return;
  const { areaId, players: nextPlayers } = snapshot.data as any;
  const currentAreaId = (area.value?.id ?? session.character?.areaId ?? null) as any;
  if (areaId && currentAreaId && areaId !== currentAreaId) return;
  players.value = (nextPlayers as { id: string; name: string; level?: number }[]) ?? [];
}

async function acknowledgeDeathAndClose() {
  if (!session.token || !session.character) return;
  const res = await apiAcknowledgeDeathEvent(session.token, session.character.id, crypto.randomUUID());
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  await loadArea();
  openSession("magic");
}

async function acknowledgeRandomEventAndClose() {
  if (!session.token || !session.character) return;
  const res = await apiAcknowledgeRandomEvent(session.token, session.character.id, crypto.randomUUID());
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  await loadArea();
}

const deathPhase = computed<"private" | "public" | null>(() => {
  if (!deathEvent.value) return null;
  const now = Date.now();
  const publicAt = parseDbUtcMillis(deathEvent.value.publicAt);
  return now < publicAt ? "private" : "public";
});

const deathMsRemainingToPublic = computed(() => {
  if (!deathEvent.value) return 0;
  return Math.max(0, parseDbUtcMillis(deathEvent.value.publicAt) - Date.now());
});

const deathMsRemainingToExpire = computed(() => {
  if (!deathEvent.value) return 0;
  return Math.max(0, parseDbUtcMillis(deathEvent.value.expiresAt) - Date.now());
});

function parseDbUtcMillis(value: string): number {
  // D1/SQLite datetime('now') yields "YYYY-MM-DD HH:MM:SS" in UTC (no timezone marker).
  // Treat it as UTC to avoid local timezone offsets inflating timers.
  if (!value) return 0;
  if (value.includes("T")) {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : 0;
  }
  const ms = Date.parse(value.replace(" ", "T") + "Z");
  return Number.isFinite(ms) ? ms : 0;
}

function formatMs(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

async function takeGroundLoot(pileId: string) {
  if (!session.token || !session.character) return;
  const pile = groundLoot.value.find((p) => p.id === pileId) || null;

  const res = await apiTakeGroundLoot(session.token, pileId, { characterId: session.character.id });
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }

  groundLoot.value = groundLoot.value.filter((p) => p.id !== pileId);

  if (pile) {
    showItemGain(pile.itemName, pile.qty);
  }
}

async function dropFromInventoryOrGold(itemId: string, qty: number) {
  if (!session.token || !session.character) return;

  const itemName =
    itemId === "item-gold"
      ? "gold"
      : session.character.inventory.find((e) => e.itemId === itemId)?.item?.name ?? "item";

  const res = await apiDropItem(session.token, session.character.id, {
    itemId,
    qty,
    clientRequestId: crypto.randomUUID()
  });

  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }

  addToast(itemId === "item-gold" ? `Dropped ${qty} gold` : `Dropped ${qty}x ${itemName}`, "info");
}

async function useConsumable(itemId: string) {
  if (!session.token || !session.character) return;

  const item = session.character.inventory.find((e) => e.itemId === itemId);
  if (!item) {
    addToast("Item not found in inventory.", "warning");
    return;
  }

  if (encounter.value && !canUseConsumable.value) {
    addToast(consumableDisabledReason.value || "Cannot use a consumable right now.", "warning");
    return;
  }

  const prevHp = session.character.hp;
  const prevMana = session.character.mana;
  const prevTempHp = session.character.temp_hp ?? 0;
  const prevHpDebuff = session.character.max_hp_debuff ?? 0;
  const prevManaDebuff = session.character.max_mana_debuff ?? 0;
  const prevEffectSums = effectSums.value;

  usingItemId.value = itemId;
  try {
    const res = await apiUseItem(session.token, session.character.id, {
      itemId,
      clientRequestId: crypto.randomUUID()
    });

    if (!res.ok) {
      addToast(res.error.message, "warning");
      return;
    }

    await loadArea({ include: AREA_POLL_INCLUDES });
    if (!session.character) return;

    const nextHp = session.character.hp;
    const nextMana = session.character.mana;
    const nextTempHp = session.character.temp_hp ?? 0;
    const nextMaxHpDebuff = session.character.max_hp_debuff ?? 0;
    const nextMaxManaDebuff = session.character.max_mana_debuff ?? 0;
    const nextEffectSums = effectSums.value;

    const hpDelta = nextHp - prevHp;
    if (hpDelta !== 0) {
      addToast(`Restored ${hpDelta} HP${hpDelta === 0 ? " (already full)" : ""}.`, hpDelta > 0 ? "success" : "info");
    }

    const manaDelta = nextMana - prevMana;
    if (manaDelta !== 0) {
      addToast(
        `Restored ${manaDelta} mana${manaDelta === 0 ? " (already full)" : ""}.`,
        manaDelta > 0 ? "success" : "info"
      );
    }

    if (nextTempHp > 0) {
      if (nextTempHp > prevTempHp) {
        addToast(`Gained ${nextTempHp - prevTempHp} temporary HP.`, "info");
      } else if (nextTempHp === prevTempHp) {
        addToast("Temporary HP refreshed.", "info");
      }
    }

    if (nextMaxHpDebuff > prevHpDebuff) {
      addToast(`Max HP reduced by ${nextMaxHpDebuff - prevHpDebuff}.`, "warning");
    }
    if (nextMaxManaDebuff > prevManaDebuff) {
      addToast(`Max mana reduced by ${nextMaxManaDebuff - prevManaDebuff}.`, "warning");
    }

    if (nextEffectSums) {
      const attrKeys = new Set([
        ...Object.keys(nextEffectSums.attr || {}),
        ...Object.keys(prevEffectSums?.attr || {})
      ]);
      for (const key of attrKeys) {
        const before = (prevEffectSums?.attr as any)?.[key] || 0;
        const after = (nextEffectSums.attr as any)?.[key] || 0;
        if (before !== after) {
          const label = String(key).toUpperCase();
          addToast(`${label} modifier is now ${after >= 0 ? "+" : ""}${after} (was ${before >= 0 ? "+" : ""}${before}).`, "info");
        }
      }

      const skillKeys = new Set([
        ...Object.keys(nextEffectSums.skill || {}),
        ...Object.keys(prevEffectSums?.skill || {})
      ]);
      for (const key of skillKeys) {
        const before = (prevEffectSums?.skill as any)?.[key] || 0;
        const after = (nextEffectSums.skill as any)?.[key] || 0;
        if (before !== after) {
          const label = String(key).charAt(0).toUpperCase() + String(key).slice(1);
          addToast(`${label} modifier is now ${after >= 0 ? "+" : ""}${after} (was ${before >= 0 ? "+" : ""}${before}).`, "info");
        }
      }
    }

    if (encounter.value) {
      let snapshot;
      try {
        snapshot = await sessionSocket.requestSnapshot("encounter");
      } catch {
        return;
      }
      if (snapshot.data.encounter) {
        encounter.value = snapshot.data.encounter as Encounter;
        startTurnTimer();
      }
    }
  } finally {
    usingItemId.value = null;
  }
}


async function handleUnequip(slot: EquipmentSlot) {
  await session.unequipItem(slot);
}

function maybeCloseTravelStartingOverlay() {
  const overlayId = travelStartingOverlayId.value;
  if (!overlayId) return;
  // If travel ended or we have successfully loaded the road area snapshot, dismiss the overlay.
  if (!travel.value) {
    overlays.close(overlayId, "api");
    travelStartingOverlayId.value = null;
    return;
  }
  const currentAreaId = (area.value?.id ?? null) as any;
  if (currentAreaId && currentAreaId === (travel.value as any).roadAreaId) {
    overlays.close(overlayId, "api");
    travelStartingOverlayId.value = null;
  }
}

function openTravelStartingOverlay(opts: {
  fromName: string;
  toName: string;
  arrivalAt: string;
  isPaused: boolean;
}) {
  // If we're upgrading an existing travel prompt modal, reuse its overlay entry so it feels like
  // the modal "turns into" a countdown overlay.
  if (travelOverlayId.value) {
    const id = travelOverlayId.value;
    overlays.update(id, {
      kind: "blocking",
      component: TravelStartingOverlay,
      props: opts
    });
    travelStartingOverlayId.value = id;
    return;
  }

  travelStartingOverlayId.value = overlays.push({
    kind: "blocking",
    closeOnEsc: true,
    component: TravelStartingOverlay,
    props: opts,
    onClose: () => {
      travelStartingOverlayId.value = null;
    }
  });
}

async function move() {
  if (!session.token || !session.character || currentLocationId.value) return;
  const res = await apiMove(session.token, session.character.id, {
    to: moveTarget.value as any,
    clientRequestId: crypto.randomUUID()
  });
  if (res.ok) {
    const toAreaId = String(moveTarget.value);
    const opt = travelOptions.value.find((o) => o.toAreaId === toAreaId) || null;
    const durationMs = opt?.durationMs ?? 20_000;
    optimisticallyStartTravel(toAreaId, durationMs);
    if (travel.value) {
      openTravelStartingOverlay({
        fromName: AREA_NAMES[travel.value.fromAreaId] || travel.value.fromAreaId,
        toName: AREA_NAMES[travel.value.toAreaId] || travel.value.toAreaId,
        arrivalAt: travel.value.arrivalAt,
        isPaused: travel.value.isPaused
      });
    }
    await loadArea();
  }
}

function openTravelModal(toAreaId: string) {
  if (!area.value) return;

  const opt = travelOptions.value.find((o) => o.toAreaId === toAreaId) || null;
  const durationMs = opt?.durationMs ?? 20_000;
  const destinationName = AREA_NAMES[toAreaId] || toAreaId;

  const disabled = inCombat.value || isInBuilding.value || isLocationTransitioning.value;
  const disabledReason = inCombat.value
    ? "Cannot travel during combat."
    : isInBuilding.value
      ? "Exit the building to travel."
      : isLocationTransitioning.value
        ? "Busy loading location."
        : null;

  if (travelOverlayId.value) {
    overlays.close(travelOverlayId.value, "api");
    travelOverlayId.value = null;
  }

  travelOverlayId.value = overlays.push({
    kind: "modal",
    component: TravelOverlay,
    props: {
      destinationName,
      durationMs,
      disabled,
      disabledReason,
      closeAfterConfirm: false,
      onConfirm: () => confirmTravel(toAreaId)
    },
    onClose: () => {
      travelOverlayId.value = null;
    }
  });
}

async function confirmTravel(toAreaId: string) {
  if (!session.token || !session.character) return;
  const res = await apiMove(session.token, session.character.id, {
    to: toAreaId as any,
    clientRequestId: crypto.randomUUID()
  });
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  // Optimistic UI: start travel immediately so the road view transitions instantly,
  // even if the follow-up area snapshot is rate-limited.
  const opt = travelOptions.value.find((o) => o.toAreaId === toAreaId) || null;
  const durationMs = opt?.durationMs ?? 20_000;
  optimisticallyStartTravel(toAreaId, durationMs);
  if (travel.value) {
    openTravelStartingOverlay({
      fromName: AREA_NAMES[travel.value.fromAreaId] || travel.value.fromAreaId,
      toName: AREA_NAMES[travel.value.toAreaId] || travel.value.toAreaId,
      arrivalAt: travel.value.arrivalAt,
      isPaused: travel.value.isPaused
    });
  }
  // Do not block the Travel overlay close on a potentially rate-limited snapshot.
  void loadArea();
}

async function restTravel() {
  if (!session.token || !session.character) return;
  const res = await apiTravelRest(session.token, session.character.id);
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  // Optimistic UI: avoid waiting on a (potentially rate-limited) area snapshot to reflect pause state.
  if (travel.value) {
    travel.value = { ...travel.value, isPaused: true };
    arrivalPollScheduled = false;
  }
  await loadArea();
}

async function walkTravel() {
  if (!session.token || !session.character) return;
  const res = await apiTravelWalk(session.token, session.character.id);
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  // Optimistic UI: resume countdown immediately so the road view transitions right away.
  if (travel.value) {
    travel.value = { ...travel.value, isPaused: false };
    arrivalPollScheduled = false;
  }
  await loadArea();
}

async function turnTravel() {
  if (!session.token || !session.character) return;
  const res = await apiTravelTurn(session.token, session.character.id);
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  await loadArea();
}

function resolveRoadAreaId(fromAreaId: string, toAreaId: string): string | null {
  const a = String(fromAreaId);
  const b = String(toAreaId);
  if ((a === "graveyard" && b === "town") || (a === "town" && b === "graveyard")) return "road_graveyard_town";
  if ((a === "town" && b === "forest") || (a === "forest" && b === "town")) return "road_town_forest";
  if ((a === "forest" && b === "city") || (a === "city" && b === "forest")) return "road_forest_city";
  return null;
}

function optimisticallyStartTravel(toAreaId: string, durationMs: number) {
  const fromAreaId = (area.value?.id ?? session.character?.areaId ?? null) as any;
  if (!fromAreaId) return;
  if (String(fromAreaId).startsWith("road_")) return;
  const roadAreaId = resolveRoadAreaId(fromAreaId, toAreaId);
  if (!roadAreaId) return;

  const clampedDurationMs = Math.max(0, Math.floor(Number(durationMs || 0)));
  const now = Date.now();
  travel.value = {
    fromAreaId,
    toAreaId: toAreaId as any,
    roadAreaId: roadAreaId as any,
    arrivalAt: new Date(now + clampedDurationMs).toISOString(),
    isPaused: false,
    remainingMs: clampedDurationMs
  } as any;
  travelRemainingMs.value = clampedDurationMs;
  arrivalPollScheduled = false;
}

async function enterLocation(locationId: string) {
  if (!session.token || !session.character) return;
  if (isLocationTransitioning.value) return;
  isLocationTransitioning.value = true;
  try {
    const res = await apiEnterLocation(session.token, session.character.id, { locationId });
    if (!res.ok) {
      addToast(res.error.message, "warning");
      return;
    }
    currentLocationId.value = locationId;
    session.character.locationId = locationId;
    // Clear encounter when entering a new location
    encounter.value = null;
    await loadArea();
  } finally {
    isLocationTransitioning.value = false;
  }
}

async function exitLocation() {
  if (!session.token || !session.character) return;
  if (isLocationTransitioning.value) return;
  isLocationTransitioning.value = true;
  try {
    const res = await apiExitLocation(session.token, session.character.id);
    if (!res.ok) {
      addToast(res.error.message, "warning");
      return;
    }
    currentLocationId.value = null;
    session.character.locationId = null;
    // Clear encounter when exiting location
    encounter.value = null;
    await loadArea();
  } finally {
    isLocationTransitioning.value = false;
  }
}

async function startCombat(npcStateId: string) {
  if (!session.token || !session.character) return;
  await loadContainers({ force: true });
  if (isRangedWeapon.value) {
    if (!equippedQuiver.value || quiverAmmoQuantity.value <= 0) {
      const proceed = await confirmDialog({
        title: "Quiver Empty",
        subtitle: "Ranged weapon equipped",
        body: "Your quiver is empty. Continue anyway and fight unarmed?",
        confirmLabel: "Fight Anyway",
        cancelLabel: "Cancel",
        variant: "default"
      });
      if (!proceed) {
        addToast("Reload or swap quivers before taking the fight.", "warning");
        return;
      }
    }
  }
  const res = await apiStartEncounter(session.token, {
    characterId: session.character.id,
    npcStateId,
    clientRequestId: crypto.randomUUID()
  });
  if (!res.ok) {
    if (res.error.code === "NPC_ENGAGED") {
      addToast("That NPC is already engaged in combat with another player.", "warning");
      return;
    }
    addToast(res.error.message, "warning");
    return;
  }
  let snapshot;
  try {
    snapshot = await sessionSocket.requestSnapshot("encounter");
  } catch {
    addToast("Failed to sync encounter state.", "warning");
    return;
  }
  const nextEncounter = snapshot.data.encounter as Encounter | null;
  if (!nextEncounter) {
    addToast("Encounter did not start.", "warning");
    return;
  }

  encounter.value = nextEncounter;
  localEncounterLog.value = [];
  encounterResolutionToastForId.value = null;

  // Enable encounter refresh cadence (time-based combat can advance while UI is open).
  startEncounterResync();

  // Start turn timer if it's player's turn
  startTurnTimer();

  // Show toasts for initial encounter log (e.g., if NPC went first)
  const initialLog = nextEncounter.log || [];
  if (initialLog.length > 0 && session.character) {
    const playerName = session.character.name;

    // Show toasts with slight delays
    initialLog.forEach((logEntry, i) => {
      const event = parseCombatEvent(logEntry, playerName);
      if (event) {
        const delay = i * 800;
        setTimeout(() => {
          if (event.isGood) {
            showCombatGood(event.message);
          } else {
            showCombatBad(event.message);
          }
        }, delay);
      }
    });
  }

  // Refresh character HP/mana in case NPC went first and dealt damage
  await loadArea();
}

function maybeShowEncounterResolutionToast(next: Encounter) {
  if (!session.character) return;
  if (next.activeTurn) return;
  if (encounterResolutionToastForId.value === next.id) return;
  encounterResolutionToastForId.value = next.id;

  const playerName = session.character.name;
  const log = next.log || [];
  const reversed = [...log].reverse();
  const terminal = reversed.find((line) => {
    const normalized = stripLeadingPictographs(String(line || ""));
    return normalized.includes("is victorious") || normalized.includes("is defeated") || normalized.includes("flees from battle");
  });

  if (terminal) {
    const event = parseCombatEvent(terminal, playerName);
    if (event) {
      if (event.isGood) showCombatGood(event.message);
      else showCombatBad(event.message);
      return;
    }
  }

  addToast("Combat resolved.", "success");
}

async function endEncounter() {
  encounter.value = null;
  localEncounterLog.value = [];
  stopTurnTimer();
  // Refresh character data to show updated XP and pending level-ups
  await loadArea();
}

// Turn timer functions
const turnAutoSubmittedForTurnStartedAt = ref<string | null>(null);

function updateTurnTimerNow() {
  if (!encounter.value?.turnStartedAt) {
    turnTimeRemaining.value = null;
    return;
  }
  if (encounter.value.activeTurn !== session.character?.id) {
    turnTimeRemaining.value = null;
    return;
  }

  const startedAt = new Date(encounter.value.turnStartedAt).getTime();
  const elapsed = (Date.now() - startedAt) / 1000;
  const remaining = Math.max(0, TURN_TIMEOUT_SECONDS - elapsed);
  turnTimeRemaining.value = remaining;

  if (remaining <= 0 && turnAutoSubmittedForTurnStartedAt.value !== encounter.value.turnStartedAt) {
    turnAutoSubmittedForTurnStartedAt.value = encounter.value.turnStartedAt;
    void submitAction("defend");
    addToast("Turn timer expired — defending automatically", "info");
  }
}

function startTurnTimer() {
  turnAutoSubmittedForTurnStartedAt.value = null;
  updateTurnTimerNow();
}

function stopTurnTimer() {
  turnTimeRemaining.value = null;
  turnAutoSubmittedForTurnStartedAt.value = null;
}

async function resyncEncounter() {
  if (!session.token || !encounter.value) return;
  const requestedCharacterId = session.character?.id ?? null;
  const encounterId = encounter.value.id;

  // Track old HP and log length for change detection
  const playerParticipant = encounter.value.participants.find(p => p.id === session.character?.id);
  const npcParticipant = encounter.value.participants.find(p => p.kind === "npc");
  const oldPlayerHp = playerParticipant?.hp || 0;
  const oldNpcHp = npcParticipant?.hp || 0;
  const oldLogLength = encounter.value.log?.length || 0;

  let snapshot;
  try {
    snapshot = await sessionSocket.requestSnapshot("encounter");
  } catch {
    return;
  }
  if (!isCurrentCharacter(requestedCharacterId) || encounter.value?.id !== encounterId) return;
  applyEncounterPayload(snapshot.data, { expectedEncounterId: encounterId, oldLogLength });
}

function applyEncounterPayload(
  payload: { encounter: Encounter | null; lootNotifications?: any[] },
  options?: { expectedEncounterId?: string; oldLogLength?: number }
) {
  const expectedEncounterId = options?.expectedEncounterId;
  const current = encounter.value;
  const oldLogLength = options?.oldLogLength ?? (current?.log?.length || 0);

  const newEncounter = payload.encounter;
  if (!newEncounter) {
    stopTurnTimer();
    // Important UX: we intentionally keep the encounter modal open after resolution so the
    // player can read the log and see the resolved state. The server may clear
    // `active_encounter_id` shortly after resolution, which makes the encounter stream return
    // `{ encounter: null }`. Do NOT clear a resolved encounter in that case (it causes flicker).
    if (current && !current.activeTurn) {
      return;
    }
    // Otherwise, treat it as the encounter being gone (dismiss modal).
    encounter.value = null;
    return;
  }

  if (expectedEncounterId && newEncounter.id !== expectedEncounterId) {
    return;
  }

  const lootNotifications = payload.lootNotifications || [];
  if (lootNotifications.length > 0) {
    for (const n of lootNotifications) {
      addToast(n.message, "success");
      localEncounterLog.value.push(n.message);
    }
  }

  const newLog = newEncounter.log || [];
  if (newLog.length > oldLogLength) {
    const newEntries = newLog.slice(oldLogLength);
    const playerName = session.character?.name || "";
    for (const entry of newEntries) {
      const event = parseCombatEvent(entry, playerName);
      if (!event) continue;
      if (event.isGood) {
        showCombatGood(event.message);
      } else {
        showCombatBad(event.message);
      }
    }
  }

  const prevTurnStartedAt = current?.turnStartedAt ?? null;
  const prevActiveTurn = current?.activeTurn ?? null;
  encounter.value = newEncounter;
  maybeShowEncounterResolutionToast(newEncounter);

  const isPlayersTurn = newEncounter.activeTurn === session.character?.id;
  if (!isPlayersTurn) {
    // Ensure we don't keep rendering a stale countdown from a prior turn.
    stopTurnTimer();
    return;
  }
  if (newEncounter.turnStartedAt && (newEncounter.turnStartedAt !== prevTurnStartedAt || newEncounter.activeTurn !== prevActiveTurn)) {
    startTurnTimer();
  }
}

function startEncounterResync() {
  // Handled by shell task orchestrator.
}

function stopEncounterResync() {
  // Handled by shell task orchestrator.
}

function inspectPlayer(_playerId: string) {
  const player = players.value.find((p) => p.id === _playerId) || null;
  openInspect({
    kind: "player",
    title: player?.name || "Player",
    subtitle: player?.level ? `Level ${player.level}` : "Level 1",
    meta: [
      { label: "Player ID", value: _playerId },
      { label: "Area", value: area.value?.id || session.character?.areaId || "unknown" }
    ],
    sections: [
      {
        title: "Overview",
        body: "This is a multiplayer preview surface. The world keeps a consistent Inspect contract even before full player interactions ship.",
        variant: "default"
      },
      { title: "Loadout", body: "Equipment, combat style, and effects will show here.", variant: "coming-soon" },
      { title: "Interaction", body: "Trade, party, duel, and chat actions will show here.", variant: "coming-soon" }
    ]
  });
}

function parseCombatEvent(
  logLine: string,
  playerName: string
): { message: string; isGood: boolean } | null {
  const lines = logLine
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return null;

  const firstLineRaw = lines[0];
  const firstLine = stripLeadingPictographs(firstLineRaw);

  if (firstLine.includes("regenerated")) {
    const regenMatch = firstLine.match(/^(.+?)\s+regenerated\s+(\d+)\s+HP\b/);
    if (regenMatch) {
      const who = regenMatch[1];
      const amount = parseInt(regenMatch[2]);
      if (who === playerName) return { message: `+${amount} HP regenerated`, isGood: true };
      return null;
    }
  }

  if (firstLine.includes("is victorious")) {
    const m = firstLine.match(/^(.+?)\s+is victorious!?/);
    const who = m?.[1] ?? playerName;
    return { message: `${who} won the battle!`, isGood: who === playerName };
  }

  if (firstLine.includes("is defeated")) {
    const m = firstLine.match(/^(.+?)\s+is defeated\.?/);
    const who = m?.[1] ?? playerName;
    return { message: `${who} was defeated!`, isGood: who !== playerName };
  }

  if (firstLine.includes("flees from battle")) {
    const m = firstLine.match(/^(.+?)\s+flees from battle\.?/);
    const who = m?.[1] ?? "Someone";
    return { message: `${who} fled from battle`, isGood: who === playerName };
  }

  if (firstLine.includes("Reloaded")) return { message: firstLine.trim(), isGood: true };
  if (firstLine.includes("Hot-swapped")) return { message: firstLine.trim(), isGood: true };
  if (firstLine.includes("recover 1×")) return { message: firstLine.trim(), isGood: true };
  if (firstLine.includes("Out of arrows") || firstLine.includes("quiver is now empty")) return { message: firstLine.trim(), isGood: false };

  const attackMatch = firstLine.match(/^(.+?)\s+attacks\s+(.+)$/);
  if (!attackMatch) return null;

  const attacker = attackMatch[1];
  const target = attackMatch[2];

  const isCritHit = lines.some((l) => l.includes("CRITICAL HIT"));
  const isCritMiss = lines.some((l) => l.includes("CRITICAL MISS"));
  const isHit = isCritHit || lines.some((l) => l.includes("- HIT!"));
  const isMiss = isCritMiss || lines.some((l) => l.includes("- MISS!"));

  const damageLine = lines.find((l) => l.includes("damage"));
  const damage = damageLine ? parseInt(damageLine.match(/^(\d+)\s+damage\b/)?.[1] ?? "0") : 0;

  const playerIsAttacker = attacker === playerName;
  const playerIsTarget = target === playerName;

  if (isHit && damage > 0) {
    const critText = isCritHit ? "critically " : "";
    if (playerIsAttacker) return { message: `${playerName} ${critText}hit ${target} for ${damage} damage`, isGood: true };
    if (playerIsTarget) return { message: `${attacker} ${critText}hit ${playerName} for ${damage} damage`, isGood: false };
  }

  if (isMiss) {
    if (playerIsAttacker) return { message: `${playerName} missed ${target}`, isGood: false };
    if (playerIsTarget) return { message: `${attacker} missed ${playerName}`, isGood: true };
  }

  return null;
}

function stripLeadingPictographs(line: string): string {
  // Strip common emoji prefixes from server log strings without depending on specific icons.
  try {
    return line.replace(/^[\p{Extended_Pictographic}\p{Emoji_Presentation}\uFE0F\u200D]+\s*/u, "");
  } catch {
    return line.replace(/^[^A-Za-z0-9]+\s*/, "");
  }
}

async function submitAction(
  action: TurnAction,
  payload?: { reloadItemId?: string | null; swapContainerInstanceId?: string | null; quantity?: number; spellId?: string | null; targetId?: string | null }
) {
  if (!session.token || !session.character || !encounter.value || turnSubmitting.value) return;

  const oldLogLength = encounter.value.log?.length || 0;
  turnSubmitting.value = true;
  let shouldRefreshContainers = false;
  let shouldRefreshArea = false;

  try {
    const res = await apiSubmitTurn(session.token, {
      encounterId: encounter.value.id,
      action,
      spellId: payload?.spellId || undefined,
      targetId: payload?.targetId || undefined,
      reloadItemId: payload?.reloadItemId || undefined,
      swapContainerInstanceId: payload?.swapContainerInstanceId || undefined,
      quantity: payload?.quantity,
      clientRequestId: crypto.randomUUID()
    });

    if (!res.ok) {
      addToast(res.error.message, "warning");
      return;
    }

    if (res.ok) {
      const data = (res.data ?? {}) as EncounterTurnResponse;
      let nextEncounter: Encounter | null = data.encounter ?? null;
      let lootNotifications = data.lootNotifications ?? [];

      if (!nextEncounter) {
        try {
          const snapshot = await sessionSocket.requestSnapshot("encounter");
          const snapshotData = snapshot.data as { encounter?: Encounter | null; lootNotifications?: any[] };
          nextEncounter = (snapshotData.encounter as Encounter | null) ?? null;
          if (!lootNotifications.length && snapshotData.lootNotifications?.length) {
            lootNotifications = snapshotData.lootNotifications;
          }
        } catch {
          addToast("Failed to sync encounter state.", "warning");
        }
      }

      if (nextEncounter) {
        encounter.value = nextEncounter;
        maybeShowEncounterResolutionToast(nextEncounter);
      } else if (encounter.value && !encounter.value.activeTurn) {
        stopEncounterResync();
        stopTurnTimer();
      } else {
        encounter.value = null;
        stopEncounterResync();
        stopTurnTimer();
      }

      // Personal loot notifications (earned on specific NPC death, but deferred drop)
      if (lootNotifications.length > 0) {
        for (const n of lootNotifications) {
          addToast(n.message, "success");
          localEncounterLog.value.push(n.message);
        }
      }

      if (nextEncounter) {
        // Parse new log entries and show toasts
        const newLog = nextEncounter.log || [];
        const newEntries = newLog.slice(oldLogLength);

        if (newEntries.length > 0 && session.character) {
          const playerName = session.character.name;

          // Show toasts with delay between player and NPC actions
          for (let i = 0; i < newEntries.length; i++) {
            const event = parseCombatEvent(newEntries[i], playerName);
            if (event) {
              // Add delay for subsequent messages (especially NPC response)
              const delay = i > 0 ? i * 800 : 0;
              setTimeout(() => {
                if (event.isGood) {
                  showCombatGood(event.message);
                } else {
                  showCombatBad(event.message);
                }
              }, delay);
            }
          }
        }

        // Stop resync and timer if combat has ended (no active turn)
        if (!nextEncounter.activeTurn) {
          stopEncounterResync();
          stopTurnTimer();
        } else {
          // Restart turn timer for player's next turn
          startTurnTimer();
        }
      }

      shouldRefreshContainers = ["attack", "reload", "hot_swap"].includes(action);
      shouldRefreshArea = true;
    }
  } finally {
    turnSubmitting.value = false;
  }

  // Post-turn refreshes are best-effort and should not block combat UX.
  // If these are slow or rate-limited, the next periodic resync will still correct state.
  if (shouldRefreshContainers) {
    void loadContainers({ force: true });
  }
  if (shouldRefreshArea) {
    void loadArea();
  }
}

async function handleReload(itemId: string | null) {
  if (!encounter.value) return;
  if (encounter.value.activeTurn !== session.character?.id) {
    addToast("Wait for your turn before reloading.", "warning");
    return;
  }
  if (!canReload.value) {
    if (reloadDisabledReason.value) addToast(reloadDisabledReason.value, "warning");
    return;
  }
  if (!itemId) {
    addToast("Choose an arrow type to reload.", "warning");
    return;
  }
  await submitAction("reload", { reloadItemId: itemId });
}

async function handleHotSwap(instanceId: string) {
  if (!encounter.value) return;
  if (encounter.value.activeTurn !== session.character?.id) {
    addToast("Wait for your turn before swapping quivers.", "warning");
    return;
  }
  if (!canHotSwap.value) {
    if (hotSwapDisabledReason.value) addToast(hotSwapDisabledReason.value, "warning");
    return;
  }
  await submitAction("hot_swap", { swapContainerInstanceId: instanceId });
}

async function castSpell(spellId: string) {
  if (!encounter.value || !session.character) return;
  if (encounter.value.activeTurn !== session.character.id) {
    addToast("Wait for your turn before casting.", "warning");
    return;
  }
  if (!spellId) return;
  await submitAction("cast", { spellId });
}

// =====================
// Gathering Functions
// =====================

const WASTE_FAILURE_MESSAGES: Record<string, string> = {
  "item-rocks": "You strike the rock, but only chip off some rocks. You gain no XP.",
  "item-splinters": "You swing wide and leave only splinters. You gain no XP.",
  "item-wilted-herbs": "You bruise the herbs into a wilted mess. You gain no XP.",
  "item-old-boot": "You haul up an old boot. You gain no XP.",
  "item-muddy-water": "You scoop the spring, but it's only muddy water. You gain no XP.",
  "item-ruined-cuts": "You botch the harvest and ruin the cuts. You gain no XP.",
  "item-tangled-fibers": "You tangle the fibers into useless knots. You gain no XP.",
  "item-spoiled-brew": "The brew spoils in your hands. You gain no XP.",
  "item-burnt-food": "You scorch the food beyond saving. You gain no XP.",
  "item-ruined-leather": "The leather tears and ruins the piece. You gain no XP.",
  "item-cracked-gemstone": "The gemstone cracks under your tools. You gain no XP.",
  "item-burnt-parchment": "The parchment scorches and crumbles. You gain no XP.",
  "item-impure-metal": "The metal cools into an impure lump. You gain no XP.",
  "item-bent-metal": "The metal bends out of shape. You gain no XP.",
  "item-wood-scraps": "Your carving slips into wood scraps. You gain no XP.",
  "item-broken-arrow": "The arrow snaps during assembly. You gain no XP.",
  "item-broken-bow": "The bowstring ruins the bow. You gain no XP.",
  "item-matted-feathers": "The feathers mat and tear apart. You gain no XP."
};

function resolveWasteFailureMessage(wasteItemId?: string | null, wasteItemName?: string | null): string {
  if (wasteItemId && WASTE_FAILURE_MESSAGES[wasteItemId]) {
    return WASTE_FAILURE_MESSAGES[wasteItemId];
  }
  const name = wasteItemName || "waste";
  return `You fumble the attempt and produce only ${name}. You gain no XP.`;
}

// Inventory optimization: for high-frequency WS resync loops, apply local deltas for simple stackables,
// and only request full inventory snapshots occasionally or when the server indicates an interrupt/complete.
let gatheringForceInventoryResync = false;
let lastGatheringInventoryResyncAtMs = 0;
let processingForceInventoryResync = false;
let lastProcessingInventoryResyncAtMs = 0;

function applyLocalInventoryDelta(itemId: string, delta: number): { applied: boolean } {
  if (!session.character?.inventory) return { applied: false };
  const list = session.character.inventory;
  // Only safe for stackable entries (no instanceIds).
  const entry = list.find((e) => e.itemId === itemId && !e.instanceIds);
  if (!entry) return { applied: false };
  entry.quantity = Math.max(0, Math.floor(Number(entry.quantity ?? 0)) + delta);
  if (entry.quantity <= 0) {
    const idx = list.indexOf(entry);
    if (idx >= 0) list.splice(idx, 1);
  }
  return { applied: true };
}

function upsertLocalStackableInventory(itemId: string, delta: number) {
  const { applied } = applyLocalInventoryDelta(itemId, delta);
  if (applied) return { applied: true, forcedResync: false };
  const list = session.character?.inventory;
  if (!list) return { applied: false, forcedResync: false };

  const existing = list.find((e) => e.itemId === itemId);
  // If the item exists but is instance-based, we cannot safely synthesize instance IDs locally.
  if (existing?.instanceIds && existing.instanceIds.length > 0) {
    return { applied: false, forcedResync: true };
  }

  // Create a minimal stack entry (no item metadata) as a temporary placeholder until the next full inventory snapshot.
  list.push({ itemId, quantity: Math.max(0, delta) } as any);
  return { applied: false, forcedResync: true };
}

async function startGathering(nodeId: string) {
  if (!session.token || !session.character || isGathering.value) return;
  lastGatheringNodeDepleted.value = false;
  const res = await apiStartGathering(
    session.token,
    session.character.id,
    nodeId,
    crypto.randomUUID()
  );
  if (res.ok) {
    gatheringResults.value = null;
    await loadArea({ include: AREA_POLL_INCLUDES_LIGHT });
    startBackgroundRefresh();
  } else {
    addToast(res.error.message, "warning");
  }
}

async function stopGathering() {
  if (!session.token || !session.character) return;
  stopBackgroundRefresh();
  await apiStopGathering(session.token, session.character.id, crypto.randomUUID());
  // Optimization: avoid a full area snapshot just to refresh inventory; fetch one targeted gathering snapshot.
  try {
    const snapshot = await sessionSocket.requestSnapshot("gathering", { includeInventory: true });
    const data = snapshot.data as any;
    if (session.character) {
      if (data.inventory) session.character.inventory = data.inventory as InventoryEntry[];
      if (typeof data.gold === "number") session.character.gold = data.gold;
    }
  } catch {
    // ignore (best-effort)
  }
  gatheringAction.value = null;
  lastGatheringNodeDepleted.value = false;
}

async function resyncGathering() {
  if (!session.token || !session.character || !gatheringAction.value) return;
  if (isActionStale(gatheringAction.value)) {
    await runOfflineSimulation("gathering-resync");
    return;
  }
  const requestedCharacterId = session.character.id;
  let snapshot;
  try {
    const now = Date.now();
    const shouldRequestInventory =
      gatheringForceInventoryResync || now - lastGatheringInventoryResyncAtMs >= 2 * 60_000;
    snapshot = await sessionSocket.requestSnapshot(
      "gathering",
      shouldRequestInventory ? { includeInventory: true } : undefined
    );
  } catch {
    return;
  }
  if (!isCurrentCharacter(requestedCharacterId)) return;
  const data = snapshot.data;
  const prevDepleted = lastGatheringNodeDepleted.value;
  const nextDepleted = !!data.nodeDepleted;
  gatheringResults.value = data;
  const interrupted = !!data.interrupt;
  const isOfflineBurst = data.ticksResolved > 1;
  const actionKey = resolveActionKey(gatheringAction.value);
  const shouldSummarizeOffline =
    isOfflineBurst && actionKey !== null && offlineGatheringSummaryActionId.value !== actionKey;

  // Per-interval notifications:
  // - If we're resolving one tick at a time, emit item + XP + failure toasts.
  // - If we resolve multiple ticks at once (burst catch-up), present a modal summary instead.
  if (isOfflineBurst) {
    if (shouldSummarizeOffline) {
      const entry = buildOfflineGatheringEntry({
        actionId: actionKey,
        results: data.results,
        totalXp: data.totalXp,
        skillId: data.skillId,
        failedTicks: data.failedTicks,
        interrupt: data.interrupt,
        actionComplete: data.actionComplete
      });
      if (entry) {
        presentOfflineSummary([entry]);
      }
      offlineGatheringSummaryActionId.value = actionKey;
    }
  } else {
    for (const result of data.results) {
      showItemGain(result.itemName, result.quantity);
    }

    if (data.failedTicks > 0) {
      const message = resolveWasteFailureMessage(data.wasteItemId, data.wasteItemName);
      for (let i = 0; i < data.failedTicks; i += 1) {
        addToast(message, "warning");
      }
    }
  }

  if (data.totalXp > 0 && data.skillId) {
    if (!isOfflineBurst) {
      showXpGain(data.skillId, data.totalXp);
    }
    // Update last used skill for recency sorting
    lastUsedSkillId.value = data.skillId;
    applyLocalSkillProgress(data.skillId, data.totalXp, data.levelUp ?? null);
  }

  if (data.levelUp) {
    showLevelUp(data.levelUp.skillId, data.levelUp.newLevel);
  }

  if (nextDepleted && !prevDepleted) {
    addToast("Resource depleted! Waiting for respawn...", "warning");
  }
  if (!nextDepleted && prevDepleted) {
    addToast("Resource replenished.", "info");
  }
  lastGatheringNodeDepleted.value = nextDepleted;

  if (gatheringAction.value) {
    gatheringAction.value = {
      ...gatheringAction.value,
      lastPolledAt: new Date().toISOString(),
      ...(data.ticksResolved > 0 ? { lastResolvedAt: new Date().toISOString() } : {})
    } as GatheringActionState;
  }

  if (data.actionComplete || interrupted) {
    stopBackgroundRefresh();
    gatheringAction.value = null;
    lastGatheringNodeDepleted.value = false;
  }
  // Update inventory/gold:
  // - Prefer authoritative snapshots when present (requested periodically or on interrupt/complete)
  // - Otherwise, apply local deltas for stackables to avoid a full inventory rebuild on the server every tick.
  if (session.character) {
    if (data.inventory) {
      session.character.inventory = data.inventory as InventoryEntry[];
      gatheringForceInventoryResync = false;
      lastGatheringInventoryResyncAtMs = Date.now();
    } else {
      let forced = false;
      for (const result of data.results || []) {
        const qty = Math.max(0, Math.floor(Number(result.quantity ?? 0)));
        if (!qty) continue;
        // Only safe for stackables; we upsert a minimal placeholder for new stacks and request a real snapshot soon.
        const applied = upsertLocalStackableInventory(result.itemId, qty);
        if (applied.forcedResync) forced = true;
      }
      // Only force a full inventory snapshot when we couldn't safely apply deltas (new item, instance-based, etc.).
      gatheringForceInventoryResync = forced;
    }
    if (typeof data.gold === "number") {
      session.character.gold = data.gold;
    }
  }
  if (interrupted) {
    await loadArea();
    if (!randomEvent.value) {
      // Best-effort: random event interruptions pause actions server-side; ensure the overlay
      // appears even if the first area refresh was rate-limited.
      scheduleRandomEventRefreshRetry();
    }
    return;
  }
}

function formatSkillLabel(skillId?: string | null) {
  if (!skillId) return null;
  return skillId.charAt(0).toUpperCase() + skillId.slice(1);
}

function applyLocalSkillProgress(skillId: SkillId, xp: number, levelUp?: { newLevel: number } | null) {
  if (!session.character?.skillProgress) return;
  const next = applySkillProgressDelta(session.character.skillProgress, {
    skillId,
    xp,
    levelUp: levelUp ?? null
  });
  session.character.skillProgress = next;
  const idx = session.characters.findIndex((entry) => entry.id === session.character?.id);
  if (idx !== -1) {
    session.characters[idx] = { ...session.characters[idx], skillProgress: next };
  }
}

function addItemLines(lines: OfflineSummaryLine[], items: { itemName?: string; quantity: number }[], fallback = "Output") {
  for (const item of items) {
    lines.push({
      text: `+${formatNumber(item.quantity)} ${item.itemName ?? fallback}`,
      tone: "info"
    });
  }
}

function buildOfflineGatheringEntry(options: {
  actionId: string;
  results: { itemName: string; quantity: number }[];
  totalXp: number;
  skillId?: string | null;
  failedTicks?: number;
  interrupt?: { kind: "combat" | "event" } | null;
  actionComplete?: boolean;
}): OfflineSummaryEntry | null {
  const lines: OfflineSummaryLine[] = [];
  addItemLines(lines, options.results, "Item");
  if (options.totalXp > 0 && options.skillId) {
    const skillName = formatSkillLabel(options.skillId);
    lines.push({
      text: `+${formatNumber(options.totalXp)} ${skillName} XP`,
      tone: "success"
    });
  }
  if (options.failedTicks && options.failedTicks > 0) {
    lines.push({
      text: `${formatNumber(options.failedTicks)} failed attempt${options.failedTicks === 1 ? "" : "s"}`,
      tone: "warning"
    });
  }
  if (options.interrupt) {
    lines.push({
      text: options.interrupt.kind === "combat" ? "Interrupted by combat." : "Interrupted by a random event.",
      tone: "warning"
    });
  }
  if (options.actionComplete) {
    lines.push({ text: "Action completed.", tone: "success" });
  }
  if (lines.length === 0) return null;
  const status = options.interrupt
    ? { label: "Interrupted", variant: "warning" as const }
    : options.actionComplete
    ? { label: "Completed", variant: "active" as const }
    : undefined;
  return {
    id: options.actionId,
    title: "Gathering",
    subtitle: formatSkillLabel(options.skillId),
    status,
    lines
  };
}

function buildOfflineProcessingEntry(options: {
  actionId: string;
  outputs: { itemName?: string; quantity: number }[];
  totalUnits?: number;
  totalXp: number;
  skillId?: string | null;
  failedTicks?: number;
  actionComplete?: boolean;
  stoppedReason?: string | null;
}): OfflineSummaryEntry | null {
  const lines: OfflineSummaryLine[] = [];
  addItemLines(lines, options.outputs);
  if (options.totalUnits && options.totalUnits > 0) {
    lines.push({ text: `${formatNumber(options.totalUnits)} units processed.`, tone: "info" });
  }
  if (options.totalXp > 0 && options.skillId) {
    const skillName = formatSkillLabel(options.skillId);
    lines.push({
      text: `+${formatNumber(options.totalXp)} ${skillName} XP`,
      tone: "success"
    });
  }
  if (options.failedTicks && options.failedTicks > 0) {
    lines.push({
      text: `${formatNumber(options.failedTicks)} failed attempt${options.failedTicks === 1 ? "" : "s"}`,
      tone: "warning"
    });
  }
  if (options.stoppedReason) {
    lines.push({ text: options.stoppedReason, tone: "warning" });
  }
  if (options.actionComplete) {
    lines.push({ text: "Batch completed.", tone: "success" });
  }
  if (lines.length === 0) return null;
  const status = options.stoppedReason
    ? { label: "Stopped", variant: "warning" as const }
    : options.actionComplete
    ? { label: "Completed", variant: "active" as const }
    : undefined;
  return {
    id: options.actionId,
    title: "Processing",
    subtitle: formatSkillLabel(options.skillId),
    status,
    lines
  };
}

function buildOfflineCastingEntry(summary: OfflineSimulationSummary & { kind: "casting" }): OfflineSummaryEntry | null {
  const lines: OfflineSummaryLine[] = [];
  if (summary.outputs?.length) {
    addItemLines(lines, summary.outputs);
  }
  if (summary.xpAwards && summary.xpAwards.length > 0) {
    for (const award of summary.xpAwards) {
      const skillName = formatSkillLabel(award.skillId);
      lines.push({
        text: `+${formatNumber(award.xp)} ${skillName} XP`,
        tone: "success"
      });
      if (award.levelUp) {
        lines.push({
          text: `Level up: ${formatSkillLabel(award.levelUp.skillId)} ${formatNumber(award.levelUp.newLevel)}.`,
          tone: "success"
        });
      }
    }
  }
  if (summary.stoppedReason) {
    lines.push({ text: summary.stoppedReason, tone: "warning" });
  }
  if (summary.completed) {
    lines.push({ text: "Casting completed.", tone: "success" });
  }
  if (lines.length === 0) return null;
  const status = summary.stoppedReason
    ? { label: "Stopped", variant: "warning" as const }
    : summary.completed
    ? { label: "Completed", variant: "active" as const }
    : undefined;
  return {
    id: summary.actionId,
    title: "Casting",
    subtitle: summary.spellName,
    status,
    lines
  };
}

function showLevelUp(skillId: string, newLevel: number) {
  const name = skillId.charAt(0).toUpperCase() + skillId.slice(1);
  addToast(`Level up! ${name} is now level ${newLevel}`, "success");
}

function clearOfflineSummaryState() {
  offlineSummaryOverlayId.value = null;
  offlineSummaryEntries.value = [];
}

function dismissOfflineSummaryOverlay() {
  if (offlineSummaryOverlayId.value) {
    overlays.close(offlineSummaryOverlayId.value, "api");
  }
  clearOfflineSummaryState();
}

function presentOfflineSummary(entries: OfflineSummaryEntry[]) {
  if (entries.length === 0) return;
  const merged = [...offlineSummaryEntries.value, ...entries];
  offlineSummaryEntries.value = merged;
  const characterName = session.character?.name ?? null;
  if (offlineSummaryOverlayId.value) {
    overlays.update(offlineSummaryOverlayId.value, {
      props: { summaries: merged, characterName }
    });
    return;
  }
  offlineSummaryOverlayId.value = overlays.push({
    kind: "modal",
    component: OfflineSummaryOverlay,
    onClose: () => clearOfflineSummaryState(),
    props: { summaries: merged, characterName }
  });
}

const OFFLINE_SIM_MIN_GAP_MS = 5000;
const OFFLINE_SIM_GAP_MULTIPLIER = 2.5;

function isActionStale(action?: { lastPolledAt?: string; lastResolvedAt?: string; startedAt?: string; intervalMs?: number; durationMs?: number; isPaused?: boolean } | null) {
  if (!action) return false;
  if (action.isPaused) return false;
  const stamps = [action.lastPolledAt, action.lastResolvedAt, action.startedAt]
    .map((stamp) => (stamp ? Date.parse(stamp) : NaN))
    .filter((value) => Number.isFinite(value));
  if (stamps.length === 0) return false;
  const lastMs = Math.max(...stamps);
  if (!Number.isFinite(lastMs)) return false;
  const gapMs = Date.now() - lastMs;
  if (!Number.isFinite(gapMs) || gapMs <= 0) return false;
  const interval = Math.max(0, Number(action.intervalMs ?? action.durationMs ?? 0));
  const threshold = Math.max(OFFLINE_SIM_MIN_GAP_MS, interval * OFFLINE_SIM_GAP_MULTIPLIER);
  return gapMs > threshold;
}

function hasActiveTimedAction() {
  if (gatheringAction.value) return true;
  if (processingAction.value && !processingAction.value.isPaused) return true;
  if (castingAction.value && !castingAction.value.isPaused) return true;
  return false;
}

function shouldSimulateOffline() {
  return (
    isActionStale(gatheringAction.value) ||
    isActionStale(processingAction.value) ||
    isActionStale(castingAction.value)
  );
}

async function runOfflineSimulation(_reason: string, options?: { force?: boolean }) {
  if (!session.token || !session.character) return;
  if (offlineSimulationInFlight.value) return;
  const shouldRun = options?.force ? hasActiveTimedAction() : shouldSimulateOffline();
  if (!shouldRun) return;

  const requestedCharacterId = session.character.id;
  offlineSimulationInFlight.value = true;

  try {
    const res = await apiSimulateOffline(session.token, session.character.id, crypto.randomUUID());
    if (!isCurrentCharacter(requestedCharacterId)) return;
    if (!res.ok) {
      addToast(res.error.message || "Offline simulation failed.", "warning");
      return;
    }
    const summaries = res.data.summaries || [];

    const entries: OfflineSummaryEntry[] = [];
    for (const summary of summaries) {
      if (summary.kind === "gather") {
        if (offlineGatheringSummaryActionId.value === summary.actionId) continue;
        const entry = buildOfflineGatheringEntry({
          actionId: summary.actionId,
          results: summary.results,
          totalXp: summary.totalXp,
          skillId: summary.skillId,
          failedTicks: summary.failedTicks,
          interrupt: summary.interrupt,
          actionComplete: summary.actionComplete
        });
        if (entry) entries.push(entry);
        offlineGatheringSummaryActionId.value = summary.actionId;
        if (summary.totalXp > 0) {
          lastUsedSkillId.value = summary.skillId;
        }
        if (summary.levelUp) {
          showLevelUp(summary.levelUp.skillId, summary.levelUp.newLevel);
        }
      } else if (summary.kind === "processing") {
        if (offlineProcessingSummaryActionId.value === summary.actionId) continue;
        const entry = buildOfflineProcessingEntry({
          actionId: summary.actionId,
          outputs: summary.outputs,
          totalUnits: summary.totalUnits,
          totalXp: summary.totalXp,
          skillId: summary.skillId,
          failedTicks: summary.failedTicks,
          actionComplete: summary.actionComplete,
          stoppedReason: summary.stoppedReason
        });
        if (entry) entries.push(entry);
        offlineProcessingSummaryActionId.value = summary.actionId;
        if (summary.totalXp > 0) {
          lastUsedSkillId.value = summary.skillId;
        }
        if (summary.levelUp) {
          showLevelUp(summary.levelUp.skillId, summary.levelUp.newLevel);
        }
      } else if (summary.kind === "casting") {
        if (offlineCastingSummaryActionId.value === summary.actionId) continue;
        const entry = buildOfflineCastingEntry(summary);
        if (entry) entries.push(entry);
        offlineCastingSummaryActionId.value = summary.actionId;
      }
    }
    if (entries.length > 0) {
      presentOfflineSummary(entries);
    }

    suppressOfflineSimulation = true;
    try {
      await loadArea({ include: AREA_POLL_INCLUDES });
    } finally {
      suppressOfflineSimulation = false;
    }
  } finally {
    offlineSimulationInFlight.value = false;
  }
}

function startBackgroundRefresh() {
  // Handled by shell task orchestrator.
}

function stopBackgroundRefresh() {
  // Handled by shell task orchestrator.
}

// =====================
// Processing Functions
// =====================

async function onStartProcessing(payload: { recipeId: string; quantity: number }) {
  if (!session.token || !session.character || processingAction.value) return;
  const res = await apiStartProcessing(
    session.token,
    session.character.id,
    payload.recipeId,
    payload.quantity,
    crypto.randomUUID()
  );
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  // Inputs may be consumed immediately; request a full inventory snapshot on the next resync tick.
  processingForceInventoryResync = true;
  await loadArea({ include: AREA_POLL_INCLUDES_LIGHT });
  startProcessingResync();
}

async function resyncProcessing() {
  if (!session.token || !session.character || !processingAction.value) return;
  if (isActionStale(processingAction.value)) {
    await runOfflineSimulation("processing-resync");
    return;
  }
  const requestedCharacterId = session.character.id;
  const actionSnapshot = processingAction.value;
  const actionKey = resolveActionKey(actionSnapshot);
  const offlineGapMs = actionSnapshot ? Date.now() - new Date(actionSnapshot.lastResolvedAt).getTime() : 0;
  let snapshot;
  try {
    const now = Date.now();
    const shouldRequestInventory =
      processingForceInventoryResync || now - lastProcessingInventoryResyncAtMs >= 30_000;
    snapshot = await sessionSocket.requestSnapshot(
      "processing",
      shouldRequestInventory ? { includeInventory: true } : undefined
    );
  } catch {
    return;
  }
  if (!isCurrentCharacter(requestedCharacterId)) return;
  const data = snapshot.data;

  const isOfflineBurst =
    data.totalUnits > 1 &&
    !!actionSnapshot &&
    offlineGapMs > Math.max(2000, actionSnapshot.durationMs * 2);
  const shouldSummarizeOffline =
    isOfflineBurst && actionKey !== null && offlineProcessingSummaryActionId.value !== actionKey;

  if (isOfflineBurst) {
    if (shouldSummarizeOffline) {
      const entry = buildOfflineProcessingEntry({
        actionId: actionKey,
        outputs: data.outputs,
        totalUnits: data.totalUnits,
        totalXp: data.totalXp,
        skillId: actionSnapshot?.skillId,
        failedTicks: data.failedTicks,
        actionComplete: data.actionComplete,
        stoppedReason: data.stoppedReason
      });
      if (entry) {
        presentOfflineSummary([entry]);
      }
      offlineProcessingSummaryActionId.value = actionKey;
    }
  } else {
    for (const output of data.outputs) {
      showItemGain(output.itemName || "Output", output.quantity);
    }
  }

  if (!isOfflineBurst && data.failedTicks > 0) {
    const message = resolveWasteFailureMessage(data.wasteItemId, data.wasteItemName);
    for (let i = 0; i < data.failedTicks; i += 1) {
      addToast(message, "warning");
    }
  }

  if (data.totalXp > 0 && processingAction.value.skillId) {
    if (!isOfflineBurst) {
      showXpGain(processingAction.value.skillId, data.totalXp);
    }
    lastUsedSkillId.value = processingAction.value.skillId;
    applyLocalSkillProgress(processingAction.value.skillId, data.totalXp, data.levelUp ?? null);
  }

  if (data.levelUp) {
    showLevelUp(data.levelUp.skillId, data.levelUp.newLevel);
  }

  if (session.character) {
    if (data.inventory) {
      session.character.inventory = data.inventory as InventoryEntry[];
      processingForceInventoryResync = false;
      lastProcessingInventoryResyncAtMs = Date.now();
    } else {
      let forced = false;
      for (const output of data.outputs || []) {
        const qty = Math.max(0, Math.floor(Number(output.quantity ?? 0)));
        if (!qty) continue;
        const applied = upsertLocalStackableInventory(output.itemId, qty);
        if (applied.forcedResync) forced = true;
      }
      // Only force a full inventory snapshot when we couldn't safely apply deltas (new item, instance-based, etc.).
      if (forced) processingForceInventoryResync = true;
    }
    if (typeof data.gold === "number") {
      session.character.gold = data.gold;
    }
  }

  if (processingAction.value) {
    processingAction.value = {
      ...processingAction.value,
      remaining: data.remaining,
      lastResolvedAt: new Date().toISOString(),
      progressMs: data.progressMs ?? processingAction.value.progressMs,
      isPaused: data.status === "paused",
      status: (data.status as any) || processingAction.value.status
    } as ProcessingAction;
  }

  if (data.stoppedReason && data.status !== "paused") {
    addToast(data.stoppedReason, "warning");
  }

  if (data.status === "paused") {
    stopProcessingResync();
    await loadArea({ include: AREA_POLL_INCLUDES_LIGHT });
    if (!randomEvent.value) {
      // Best-effort: processing can be paused by a random event. If the area snapshot refresh
      // failed (rate-limited), retry soon so the overlay appears promptly.
      scheduleRandomEventRefreshRetry();
    }
    return;
  }

  if (data.actionComplete || data.status === "stopped" || data.stoppedReason) {
    stopProcessingResync();
    processingAction.value = null;
    await loadArea({ include: AREA_POLL_INCLUDES_LIGHT });
  }
}

async function cancelProcessing() {
  if (!session.token || !session.character) return;
  stopProcessingResync();
  await apiStopProcessing(session.token, session.character.id);
  processingAction.value = null;
  // Optimization: avoid a full area snapshot just to refresh inventory; fetch one targeted processing snapshot.
  try {
    const snapshot = await sessionSocket.requestSnapshot("processing", { includeInventory: true });
    const data = snapshot.data as any;
    if (session.character) {
      if (data.inventory) session.character.inventory = data.inventory as InventoryEntry[];
      if (typeof data.gold === "number") session.character.gold = data.gold;
    }
  } catch {
    // ignore (best-effort)
  }
  await loadArea({ include: AREA_POLL_INCLUDES_LIGHT });
}

async function pauseProcessing() {
  if (!session.token || !session.character || !processingAction.value) return;
  const res = await apiPauseProcessing(session.token, session.character.id);
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  stopProcessingResync();
  processingAction.value = { ...processingAction.value, isPaused: true, status: "paused" } as ProcessingAction;
}

async function resumeProcessing() {
  if (!session.token || !session.character || !processingAction.value) return;
  const res = await apiResumeProcessing(session.token, session.character.id);
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  processingAction.value = { ...processingAction.value, isPaused: false, status: "active" } as ProcessingAction;
  startProcessingResync();
}

function startProcessingResync() {
  // Handled by shell task orchestrator.
}

function stopProcessingResync() {
  // Handled by shell task orchestrator.
}

async function resumeCasting() {
  if (!session.token || !session.character) return;
  if (!castingAction.value) return;
  const res = await apiResumeMagic(session.token, { characterId: session.character.id });
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  await loadArea();
}

async function cancelCasting() {
  if (!session.token || !session.character) return;
  if (!castingAction.value) return;
  const res = await apiStopMagic(session.token, { characterId: session.character.id });
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  await loadArea();
}

async function castFromSession(spellId: string) {
  if (!session.token || !session.character) return;
  const res = await apiMagicCast(session.token, {
    characterId: session.character.id,
    spellId,
    clientRequestId: crypto.randomUUID()
  });
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  await loadArea();
}

async function readySpell(spellId: string) {
  if (!session.token || !session.character) return;
  const res = await apiReadySpell(session.token, {
    characterId: session.character.id,
    spellId,
    clientRequestId: crypto.randomUUID()
  });
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  await loadArea();
}

async function unreadySpell(spellId: string) {
  if (!session.token || !session.character) return;
  const res = await apiUnreadySpell(session.token, {
    characterId: session.character.id,
    spellId,
    clientRequestId: crypto.randomUUID()
  });
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  await loadArea();
}

// =====================
// Enchanting Functions
// =====================

function findInstanceName(instanceId: string): string {
  const entry = session.character?.inventory?.find((inv) => inv.instanceIds?.includes(instanceId));
  return entry?.item?.name || "item";
}

async function applyEnchantment(payload: { instanceId: string; enchantmentId: string }) {
  if (!session.token || !session.character) return;
  const name = findInstanceName(payload.instanceId);
  const prevSkill = session.character.skillProgress?.find((s) => s.id === "enchanting") ?? null;
  const prevSkillXp = prevSkill?.xp ?? 0;
  const prevSkillLevel = prevSkill?.level ?? 0;
  const res = await apiEnchantItem(session.token, session.character.id, {
    ...payload,
    clientRequestId: crypto.randomUUID()
  });
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  addToast(`Enchanted ${name}`, "success");
  await loadArea({ include: AREA_POLL_INCLUDES });
  if (!session.character) return;

  const nextSkill = session.character.skillProgress?.find((s) => s.id === "enchanting") ?? null;
  const nextXp = nextSkill?.xp ?? prevSkillXp;
  const nextLevel = nextSkill?.level ?? prevSkillLevel;
  const gainedXp = Math.max(0, Math.floor(Number(nextXp) - Number(prevSkillXp)));
  if (gainedXp > 0) {
    showXpGain("enchanting", gainedXp);
    lastUsedSkillId.value = "enchanting";
  }
  if (nextLevel > prevSkillLevel) {
    showLevelUp("enchanting", nextLevel);
  }
}

async function removeEnchantment(payload: { instanceId: string }) {
  if (!session.token || !session.character) return;
  const name = findInstanceName(payload.instanceId);
  const prevSkill = session.character.skillProgress?.find((s) => s.id === "enchanting") ?? null;
  const prevSkillXp = prevSkill?.xp ?? 0;
  const prevSkillLevel = prevSkill?.level ?? 0;
  const res = await apiDisenchantItem(session.token, session.character.id, {
    ...payload,
    clientRequestId: crypto.randomUUID()
  });
  if (!res.ok) {
    addToast(res.error.message, "warning");
    return;
  }
  addToast(`Disenchanted ${name}`, "info");
  await loadArea({ include: AREA_POLL_INCLUDES });
  if (!session.character) return;

  const nextSkill = session.character.skillProgress?.find((s) => s.id === "enchanting") ?? null;
  const nextXp = nextSkill?.xp ?? prevSkillXp;
  const nextLevel = nextSkill?.level ?? prevSkillLevel;
  const gainedXp = Math.max(0, Math.floor(Number(nextXp) - Number(prevSkillXp)));
  if (gainedXp > 0) {
    showXpGain("enchanting", gainedXp);
    lastUsedSkillId.value = "enchanting";
  }
  if (nextLevel > prevSkillLevel) {
    showLevelUp("enchanting", nextLevel);
  }
}

function registerShellResyncTasks() {
  shellTasks.registerTask({
    id: "processing-ui-now",
    label: "Processing UI timebase",
    intervalMs: 250,
    enabled: () => !!processingAction.value && !processingAction.value.isPaused,
    run: () => {
      processingUiNowMs.value = Date.now();
    }
  });

  shellTasks.registerTask({
    id: "travel-countdown",
    label: "Travel countdown",
    intervalMs: 1000,
    enabled: () => !!travel.value,
    run: () => {
      if (!travel.value) return;
      if (travel.value.isPaused) return;
      travelRemainingMs.value = Math.max(0, travelRemainingMs.value - 1000);
      if (travelRemainingMs.value <= 0 && !arrivalPollScheduled) {
        arrivalPollScheduled = true;
        if (!isLocationTransitioning.value) {
          // The client countdown is now at 0; show a clear "loading location" overlay while we wait for the server
          // to resolve travel and provide the destination snapshot.
          isLocationTransitioning.value = true;
        }
        void loadArea({ include: AREA_POLL_INCLUDES });
      }
    }
  });

  shellTasks.registerTask({
    id: "turn-timer",
    label: "Turn timer",
    intervalMs: 100,
    enabled: () => !!encounter.value && encounter.value.activeTurn === session.character?.id,
    run: () => updateTurnTimerNow()
  });

  shellTasks.registerTask({
    id: "vitals-resync",
    label: "Vitals resync (HP/Mana regen)",
    intervalMs: VITALS_RESYNC_MS,
    enabled: () =>
      !!session.token &&
      !!session.character &&
      sessionSocket.isConnected.value &&
      !isLocationTransitioning.value &&
      !isShellLoading.value,
    run: async () => {
      await resyncVitals();
    }
  });

  shellTasks.registerTask({
    id: "area-resync-area",
    label: "Area resync (Area session)",
    intervalMs: AREA_RESYNC_MS_AREA,
    enabled: () =>
      !!session.token &&
      !!session.character &&
      sessionSocket.isConnected.value &&
      activeSession.value === "area" &&
      !isLocationTransitioning.value &&
      !isShellLoading.value &&
      !encounter.value,
    staleAfterMs: AREA_RESYNC_MS_AREA * 2 + 1000,
    run: async () => {
      await loadArea({ include: AREA_POLL_INCLUDES });
    }
  });

  shellTasks.registerTask({
    id: "area-resync-other",
    label: "Area resync (other sessions)",
    intervalMs: AREA_RESYNC_MS_OTHER,
    enabled: () =>
      !!session.token &&
      !!session.character &&
      sessionSocket.isConnected.value &&
      activeSession.value !== "area" &&
      !isLocationTransitioning.value &&
      !isShellLoading.value &&
      !encounter.value,
    staleAfterMs: AREA_RESYNC_MS_OTHER * 2 + 1000,
    run: async () => {
      await loadArea({ include: AREA_POLL_INCLUDES });
    }
  });

  shellTasks.registerTask({
    id: "encounter-resync",
    label: "Encounter resync",
    intervalMs: ENCOUNTER_RESYNC_MS,
    enabled: () => !!session.token && sessionSocket.isConnected.value && !!encounter.value && !!encounter.value.activeTurn,
    staleAfterMs: Math.max(15000, ENCOUNTER_RESYNC_MS * 3),
    run: async () => {
      await resyncEncounter();
    }
  });

  shellTasks.registerTask({
    id: "gathering-resync",
    label: "Gathering resync",
    intervalMs: 2000,
    enabled: () => !!session.token && sessionSocket.isConnected.value && !!session.character && !!gatheringAction.value,
    run: async () => {
      await resyncGathering();
    }
  });

  shellTasks.registerTask({
    id: "loot-resync",
    label: "Loot resync",
    intervalMs: LOOT_RESYNC_MS,
    enabled: () =>
      !!session.token &&
      !!session.character &&
      sessionSocket.isConnected.value &&
      activeSession.value === "area" &&
      !isLocationTransitioning.value,
    run: async () => {
      await resyncLoot();
    }
  });

  shellTasks.registerTask({
    id: "presence-resync",
    label: "Presence resync",
    intervalMs: PRESENCE_RESYNC_MS,
    enabled: () =>
      !!session.token &&
      !!session.character &&
      sessionSocket.isConnected.value &&
      activeSession.value === "area" &&
      !isLocationTransitioning.value,
    run: async () => {
      await resyncPresence();
    }
  });

  shellTasks.registerTask({
    id: "processing-resync",
    label: "Processing resync",
    intervalMs: 2000,
    enabled: () =>
      !!session.token &&
      sessionSocket.isConnected.value &&
      !!session.character &&
      !!processingAction.value &&
      !processingAction.value.isPaused,
    run: async () => {
      await resyncProcessing();
    }
  });
}

function startAreaResync() {
  // Handled by shell task orchestrator.
}

function stopAreaResync() {
  // Handled by shell task orchestrator.
}

// Level-up and AP spending functions
function getCharacterXp(): number {
  const characterSkill = session.character?.skillProgress?.find(s => s.id === 'character');
  return characterSkill?.xp ?? 0;
}

function getCharacterLevel(): number {
  const characterSkill = session.character?.skillProgress?.find(s => s.id === 'character');
  return characterSkill?.level ?? 1;
}

function getLevelAfterLevelUp(): number {
  return getCharacterLevel() + 1;
}

function getNextCharacterLevelXp(): number {
  return combatXpForLevel(getCharacterLevel() + 1);
}

function getApAwardedForLevel(level: number): number {
  const asiLevels = [4, 8, 12, 16, 19];
  return asiLevels.includes(level) ? 1 : 0;
}

async function performLevelUp() {
  if (!session.token || !session.character) return false;

  const result = await apiLevelUp(session.token, session.character.id);

  if (!result.ok) {
    addToast(result.error?.message || 'Failed to level up', 'error');
    return false;
  }

  // Refresh character data
  await loadArea();
  return true;
}

async function handleLevelUpContinue() {
  // User confirmed level-up, perform it now
  const success = await performLevelUp();
  if (success) {
    addToast('Level up complete!', 'success');
  }
  // User can click "Level-Up(s) Ready!" button again for next levelup
}

function openLevelUpOverlay() {
  if (!session.character) return;
  if (levelUpOverlayId.value) return;

  levelUpOverlayId.value = overlays.push({
    kind: "modal",
    component: LevelUpOverlay,
    props: {
      characterName: session.character.name,
      newLevel: getCharacterLevel() + 1,
      currentXp: getCharacterXp(),
      nextLevelXp: getNextCharacterLevelXp(),
      apAwarded: getApAwardedForLevel(getCharacterLevel() + 1),
      totalAp: (session.character.apAvailable ?? 0) + getApAwardedForLevel(getCharacterLevel() + 1),
      remainingLevelups: Math.max(0, (session.character.pendingLevelups ?? 0) - 1),
      onContinue: handleLevelUpContinue,
      onSpendAp: () => openSpendApOverlay({ performPendingLevelup: true })
    },
    onClose: () => {
      levelUpOverlayId.value = null;
    }
  });
}

async function openSpendApOverlay(options?: { performPendingLevelup?: boolean }) {
  if (!session.character) return;
  if (spendApOverlayId.value) return;

  if (options?.performPendingLevelup) {
    const success = await performLevelUp();
    if (!success) return;
    addToast("Level up complete!", "success");
  }

  spendApOverlayId.value = overlays.push({
    kind: "modal",
    component: SpendApOverlay,
    props: {
      currentAttributes: session.character.attributes,
      totalAp: session.character.apAvailable ?? 0,
      onSpend: handleSpendAp
    },
    onClose: () => {
      spendApOverlayId.value = null;
    }
  });
}

async function handleSpendAp(increments: Record<string, number>) {
  if (!session.token || !session.character) return;

  const result = await apiSpendAp(session.token, session.character.id, increments);

  if (!result.ok) {
    addToast(result.error?.message || 'Failed to spend AP', 'error');
    return;
  }

  // Update character state immediately so the Stats panel reflects changes without waiting
  session.character.attributes = result.data.attributes;
  session.character.apAvailable = result.data.apRemaining;
  session.character.max_hp = result.data.max_hp;
  session.character.max_mana = result.data.max_mana;

  // Clamp current HP/mana to new caps (defensive)
  session.character.hp = Math.min(session.character.hp, session.character.max_hp);
  session.character.mana = Math.min(session.character.mana, session.character.max_mana);

  // Still refresh area data (inventory/gold/hp regen etc.)
  await loadArea();
  addToast('Attributes increased!', 'success');
  // User can click "Level-Up(s) Ready!" button again for next levelup
}

watch(
  () => route.query.session,
  (sessionId) => {
    if (!sessionId || Array.isArray(sessionId)) return;
    const match = sessionItems.find((item) => item.id === sessionId);
    if (match) {
      openSession(match.id);
    }
  },
  { immediate: true }
);

watch(
  () => [processingAction.value, castingAction.value] as const,
  ([processing, casting]) => {
    if (stickyActionSessionOpened.value) return;
    if (processing) {
      stickyActionSessionOpened.value = true;
      openSession("workshop");
      return;
    }
    if (casting) {
      stickyActionSessionOpened.value = true;
      openSession("magic");
    }
  }
);

onMounted(() => {
  loadLayoutPrefs();
  mobileMql = window.matchMedia("(max-width: 900px)");
  handleMobileMqlChange(mobileMql);
  mobileMql.addEventListener("change", handleMobileMqlChange);

  // Drawer uses a sheet-style UI on mobile viewports.
  drawerSheetMql = window.matchMedia("(max-width: 900px)");
  handleDrawerSheetMqlChange(drawerSheetMql);
  drawerSheetMql.addEventListener("change", handleDrawerSheetMqlChange);

  window.addEventListener("keydown", handleKeydown);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  syncBrowserOfflineFromNavigator();
  window.addEventListener("online", handleBrowserOnline);
  window.addEventListener("offline", handleBrowserOffline);
  if (import.meta.env.DEV) {
    window.__idleRpgSessionSocket = sessionSocket;
  }
  registerShellResyncTasks();

  wsUnsubscribers.push(
    sessionSocket.onEvent("encounter", (msg) => {
      if (msg.event !== "encounter.updated") return;
      const payload = msg.data;
      if (!payload || typeof payload !== "object") return;
      const expectedEncounterId = encounter.value?.id;
      if (!expectedEncounterId) {
        localEncounterLog.value = [];
        applyEncounterPayload(payload as any);
        return;
      }
      applyEncounterPayload(payload as any, { expectedEncounterId });
    })
  );
  wsUnsubscribers.push(
    sessionSocket.onSnapshot("loot", (msg) => {
      const { areaId, groundLoot: nextLoot, deathEvent: nextDeath } = msg.data as any;
      const currentAreaId = (area.value?.id ?? session.character?.areaId ?? null) as any;
      if (areaId && currentAreaId && areaId !== currentAreaId) return;
      groundLoot.value = (nextLoot as GroundLootPile[]) ?? [];
      deathEvent.value = (nextDeath as DeathEvent | null) ?? null;
    })
  );
  wsUnsubscribers.push(
    sessionSocket.onSnapshot("presence", (msg) => {
      const { areaId, players: nextPlayers } = msg.data as any;
      const currentAreaId = (area.value?.id ?? session.character?.areaId ?? null) as any;
      if (areaId && currentAreaId && areaId !== currentAreaId) return;
      players.value = (nextPlayers as { id: string; name: string; level?: number }[]) ?? [];
    })
  );
  wsUnsubscribers.push(
    sessionSocket.onEvent("loot", (msg) => {
      if (msg.event !== "loot.updated") return;
      void shellTasks.trigger("loot-resync");
    })
  );
  wsUnsubscribers.push(
    sessionSocket.onEvent("area", (msg) => {
      if (msg.event !== "area.changed") return;
      void loadArea({ include: AREA_POLL_INCLUDES });
    })
  );
  wsUnsubscribers.push(sessionSocket.onEvent("gathering", () => void shellTasks.trigger("gathering-resync")));
  wsUnsubscribers.push(sessionSocket.onEvent("processing", () => void shellTasks.trigger("processing-resync")));

  // Process initial area snapshot from subscription (avoids duplicate resync)
  wsUnsubscribers.push(
    sessionSocket.onSnapshot("area", (msg) => {
      const wasLoading = isShellLoading.value;
      const wasSwapping = isCharacterSwapping.value;
      if (wasLoading) {
        clearAreaBootstrapTimers();
        areaBootstrapError.value = null;
      }
      // Clear the swapping flag after receiving the first snapshot.
      // This ensures subsequent snapshots during normal gameplay are treated normally.
      if (wasSwapping) {
        isCharacterSwapping.value = false;
      }
      // IMPORTANT:
      // - During bootstrap (not character swap), we treat the subscription snapshot as "static-capable"
      //   so the loading overlay doesn't clear on core-only refreshes (e.g., vitals).
      // - During character swap, the server's automatic snapshot uses cached streamParams which may not
      //   include static data. We pass hasStatic=false so loading clears when we have basic area data.
      // - During normal runtime, area stream snapshots may be "core-only" or omit static payloads; do not
      //   force `hasStatic=true` or we'll incorrectly warn about missing locations during travel.
      void processAreaSnapshot(msg.data, {
        hasStatic: wasLoading && !wasSwapping,
        showLoading: wasLoading,
        loadAreaToken: wasLoading ? loadAreaToken : 0
      });
    })
  );

  handleVisibilityChange();
  shellTasks.start();
  if (!session.character) {
    router.push("/characters");
  } else {
    // Set initial move target to first adjacent area
    const adjacent = AREA_ADJACENCY[session.character.areaId] || [];
    moveTarget.value = adjacent[0] || "town";

    // WS handlers are now registered; it's safe to connect + subscribe without missing the first snapshot.
    wsHandlersReady.value = true;
    if (session.token && session.character.id) {
      sessionSocket.connect({ token: session.token, characterId: session.character.id });
      sessionSocket.subscribe([...coreWsStreams]);
    }

    void loadContainers({ force: true });
    // Prefer the subscription snapshot for the initial render (avoids duplicate snapshots / rate limiting).
    // If it doesn't arrive promptly, fall back to an explicit snapshot request.
    isShellLoading.value = true;
    areaBootstrapError.value = null;
    scheduleInitialAreaBootstrapFallback();
    startAreaResync();
  }
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeydown);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  window.removeEventListener("online", handleBrowserOnline);
  window.removeEventListener("offline", handleBrowserOffline);
  if (window.__idleRpgSessionSocket === sessionSocket) delete window.__idleRpgSessionSocket;
  while (wsUnsubscribers.length) {
    try {
      wsUnsubscribers.pop()?.();
    } catch {
      // ignore
    }
  }
  if (stickyLevelupToastId.value != null) removeToast(stickyLevelupToastId.value);
  if (stickyApToastId.value != null) removeToast(stickyApToastId.value);
  if (mobileMql) {
    mobileMql.removeEventListener("change", handleMobileMqlChange);
    mobileMql = null;
  }
  if (drawerSheetMql) {
    drawerSheetMql.removeEventListener("change", handleDrawerSheetMqlChange);
    drawerSheetMql = null;
  }
  if (resizingDrawer.value) handleDrawerResizeEnd();
  stopBackgroundRefresh();
  stopAreaResync();
  stopEncounterResync();
  stopProcessingResync();
  shellTasks.stop();
  clearAreaBootstrapTimers();
});
</script>

<style scoped>
.shell {
  display: grid;
  grid-template-columns: var(--shell-rail) minmax(0, 1fr) var(--drawer-spine);
  height: 100vh;
  min-height: 0;
  position: relative;
  overflow: hidden;
  --mobile-nav-height: 0px;
  --mobile-nav-offset: env(safe-area-inset-bottom);
  --mobile-nav-gap: 1.5rem;
}

.shell.loading .shell-world {
  filter: saturate(0.9);
  opacity: 0.85;
  pointer-events: none;
}

.shell-loading {
  position: fixed;
  inset: 0;
  background: var(--overlay-scrim);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
  backdrop-filter: blur(4px);
}

.shell-loading__card {
  background: var(--color-panel-strong);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  padding: 1.5rem 2rem;
  display: grid;
  gap: 0.6rem;
  text-align: center;
  box-shadow: var(--shadow-md);
}

.shell-rail {
  position: sticky;
  top: 0;
  height: 100vh;
  min-height: 0;
  overflow: auto;
  padding: 1.5rem 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  border-right: 1px solid var(--color-border);
  background: color-mix(in srgb, var(--bg-0) 62%, transparent);
  backdrop-filter: blur(12px);
}

.rail-brand {
  display: grid;
  gap: 0.3rem;
}

.rail-brand__tag {
  font-size: 0.65rem;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--color-muted);
}

.rail-brand__title {
  font-family: var(--font-display);
  font-size: 1.1rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.rail-identity {
  display: grid;
  gap: 0.75rem;
}

.rail-identity__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.rail-identity__name {
  font-size: 1.2rem;
  font-weight: 600;
}

.rail-identity__meta {
  color: var(--color-muted);
}

.rail-vitals {
  display: grid;
  gap: 0.5rem;
}

.rail-vital {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: var(--color-muted);
}

.rail-activity {
  display: grid;
  gap: 0.4rem;
}

.rail-activity__text {
  font-size: 0.9rem;
  color: var(--color-text);
}

.rail-cta {
  display: grid;
  gap: 0.5rem;
}

.rail-status {
  display: grid;
  gap: 0.8rem;
}

.rail-status__panel {
  display: grid;
  gap: 0.8rem;
}

.rail-status__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
}

.rail-status__item {
  display: grid;
  gap: 0.5rem;
  padding: 0.65rem 0.75rem;
  border-radius: var(--radius-1);
  background: color-mix(in srgb, var(--bg-2) 80%, transparent);
  border: 1px solid var(--border-0);
}

.rail-status__title {
  font-weight: 750;
}

.rail-status__desc {
  font-size: 0.85rem;
}

.rail-status__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.rail-status__empty {
  font-size: 0.85rem;
}

.rail-utility {
  margin-top: auto;
  display: grid;
  gap: 0.6rem;
}

.shell-world {
  padding: 1.5rem 2rem 6rem;
  min-height: 0;
  overflow: auto;
}

.world-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 380px);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.world-header__actions {
  display: grid;
  gap: 1rem;
  justify-items: end;
}

.world-header h2 {
  font-family: var(--font-display);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin: 0 0 0.4rem;
}

.world-header__location {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.6rem;
}

.world-header__warning {
  color: var(--color-warning);
  font-size: 0.85rem;
  margin-bottom: 0.6rem;
}

.travel-banner {
  margin-top: 1rem;
  padding: 0.8rem 1rem;
  border-radius: var(--radius-md);
  border: 1px solid color-mix(in srgb, var(--accent-travel) 28%, var(--border-0));
  background: color-mix(in srgb, var(--accent-travel) 10%, var(--glass-0));
  display: grid;
  gap: 0.6rem;
}

.travel-banner__route {
  font-weight: 600;
}

.travel-eta {
  font-size: 0.95rem;
  color: var(--color-muted);
}

.world-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 360px));
  justify-content: start;
  gap: 1rem;
}

.world-grid > * {
  min-width: 0;
}

.encounter-float {
  position: fixed;
  left: calc(var(--shell-rail) + 1.5rem);
  right: calc(var(--drawer-spine) + 1.5rem);
  top: 1.5rem;
  bottom: calc(env(safe-area-inset-bottom) + 1.5rem);
  z-index: 3400;
  display: flex;
  pointer-events: none;
}

.encounter-float :deep(.encounter-surface) {
  pointer-events: auto;
  width: 100%;
  max-height: 100%;
  overflow: auto;
}

.drawer-scrim {
  position: fixed;
  inset: 0;
  background: var(--sheet-scrim);
  backdrop-filter: blur(3px);
  z-index: 3550;
}

.shell-drawer {
  position: sticky;
  top: 0;
  height: 100vh;
  width: var(--drawer-spine);
  z-index: 3650;
}

.drawer-spine {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  overflow-y: auto;
  padding: 1rem 0.5rem;
  gap: 1rem;
  border-left: 1px solid var(--color-border);
  background: color-mix(in srgb, var(--bg-0) 72%, transparent);
  backdrop-filter: blur(10px);
}

.drawer-toggle {
  border-radius: var(--radius-pill);
}

.drawer-spine__status {
  width: 100%;
  display: grid;
  gap: 0.6rem;
  padding: 0 0.1rem;
  min-height: 0;
}

.spine-stat {
  display: grid;
  justify-items: center;
  gap: 0.15rem;
  padding: 0.35rem 0.25rem;
  border-radius: var(--radius-1);
  background: var(--glass-0);
  border: 1px solid var(--glass-border-0);
}

.spine-stat__label {
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--fg-1);
}

.spine-stat__value {
  font-size: 0.85rem;
  font-weight: 800;
}

.spine-paused-chip {
  justify-content: center;
  max-width: 100%;
  text-align: center;
  white-space: normal;
  line-height: 1.1;
  padding-inline: 0.4rem;
  font-size: 0.72rem;
}

.drawer-spine__quick {
  width: 100%;
  display: grid;
  gap: 0.5rem;
  justify-items: center;
  min-height: 0;
}

.drawer-icons {
  display: grid;
  gap: 0.6rem;
}

.drawer-icon {
  border-radius: var(--radius-sm);
  border: 1px solid transparent;
  width: 2.4rem;
  height: 2.4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--glass-1);
  color: var(--color-text);
}

.drawer-icon.active {
  border-color: color-mix(in srgb, var(--accent) 60%, transparent);
  box-shadow: var(--shadow-accent);
}

.drawer-panel {
  position: fixed;
  top: 0;
  right: var(--drawer-spine);
  height: 100vh;
  width: var(--drawer-width);
  max-width: var(--drawer-width-max);
  background: color-mix(in srgb, var(--bg-2) 92%, transparent);
  border-left: 1px solid var(--color-border);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow: hidden;
  transform: translateX(100%);
  opacity: 0;
  pointer-events: none;
  transition: transform var(--duration-base) var(--ease-out), opacity var(--dur-2) var(--ease-out);
  will-change: transform;
}

.shell-drawer.open .drawer-panel {
  transform: translateX(0);
  opacity: 1;
  pointer-events: auto;
}

.drawer-resize {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 10px;
  cursor: col-resize;
  background: linear-gradient(90deg, color-mix(in srgb, var(--border-1) 70%, transparent), transparent);
  opacity: 0.35;
}

.drawer-resize:hover {
  opacity: 0.65;
}

.drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.drawer-header h3 {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.drawer-content {
  flex: 1;
  display: grid;
  gap: 1rem;
  min-height: 0;
}

.stats-snapshot__grid {
  display: grid;
  gap: 1.5rem;
}

.stats-label {
  font-size: 0.8rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--color-muted);
  margin-bottom: 0.4rem;
}

.stats-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.area-session {
  display: grid;
  gap: 1rem;
}

.area-session__meta {
  display: grid;
  gap: 0.75rem;
}

.area-session__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 0.75rem;
}

.area-session__travel {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.session-placeholder {
  display: grid;
  gap: 1rem;
  color: var(--color-muted);
}

.session-card {
  display: grid;
  gap: 0.8rem;
}

.session-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.shell-empty {
  padding: 3rem;
}

.area-layout {
  display: flex;
  gap: 0;
  min-height: calc(100vh - 80px);
  position: relative;
}

.area-layout.loading .area-main,
.area-layout.loading .area-sidebar {
  pointer-events: none;
  filter: saturate(0.85);
  opacity: 0.85;
}

@media (max-width: 900px) {
  .shell {
    grid-template-columns: 1fr;
    --mobile-nav-height: 4.5rem;
    --mobile-nav-offset: calc(env(safe-area-inset-bottom) + var(--mobile-nav-height));
    --mobile-nav-gap: 1.2rem;
  }

  .world-header {
    grid-template-columns: 1fr;
  }

  .shell-world {
    padding: 1.2rem 1.2rem calc(var(--mobile-nav-offset) + var(--mobile-nav-gap));
    scroll-padding-bottom: calc(var(--mobile-nav-offset) + var(--mobile-nav-gap));
  }

  .encounter-float {
    left: 1.2rem;
    right: 1.2rem;
    top: 1.2rem;
    bottom: calc(var(--mobile-nav-offset) + var(--mobile-nav-gap));
  }

  .shell-rail {
    position: fixed;
    bottom: 0;
    top: auto;
    width: 100%;
    height: auto;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem calc(env(safe-area-inset-bottom) + 0.75rem);
    border-bottom: none;
    border-top: 1px solid var(--color-border);
    z-index: 3950;
  }

  .rail-brand {
    display: none;
  }

  .rail-identity,
  .rail-cta {
    display: none;
  }

  .mobile-nav {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 0.35rem;
  }

  .mobile-nav__item {
    display: grid;
    justify-items: center;
    gap: 0.1rem;
    padding: 0.5rem 0.25rem;
    border-radius: var(--radius-1);
    border: 1px solid transparent;
    color: var(--fg-1);
  }

  .mobile-nav__item.active {
    color: var(--fg-0);
    border-color: color-mix(in srgb, var(--accent) 28%, transparent);
    background: color-mix(in srgb, var(--accent) 10%, transparent);
  }

  .mobile-nav__icon {
    font-size: 1.15rem;
    line-height: 1;
  }

  .mobile-nav__label {
    font-size: 0.62rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }
}

.sheet-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0.15rem 0.9rem;
  border-bottom: 1px solid color-mix(in srgb, var(--border-0) 85%, transparent);
  position: sticky;
  top: 0;
  z-index: 5;
  background: color-mix(in srgb, var(--bg-2) 94%, transparent);
  backdrop-filter: blur(10px);
}

.sheet-title {
  font-family: var(--font-display);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
}

.sheet-subtitle {
  margin-top: 0.25rem;
  font-size: 0.9rem;
}

.area-loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 900;
  background: var(--overlay-scrim);
  display: grid;
  place-items: center;
  backdrop-filter: blur(3px);
}

.area-loading-card {
  width: min(420px, 92vw);
  background: color-mix(in srgb, var(--bg-2) 92%, transparent);
  border: 1px solid var(--border-0);
  border-radius: 14px;
  padding: 1rem;
  display: grid;
  gap: 0.35rem;
  justify-items: center;
  text-align: center;
  box-shadow: var(--shadow-2);
}

.spinner {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid color-mix(in srgb, var(--border-1) 75%, transparent);
  border-top-color: var(--accent-travel);
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.area-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  padding-right: 1rem;
}

.area-panels {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  align-items: start;
}

.social-close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 2;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
